import React, { useState, useEffect, useRef, useCallback } from "react";
import * as XLSX from 'xlsx';
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Skeleton,
  Alert,
  TextField,
  InputAdornment,
  Drawer,
  Tabs,
  Tab,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  FormControlLabel,
  TableSortLabel,
  Pagination,
  FormControl,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Navbar from "../components/Navbar.js";
import { backendRoot } from "../config";
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import InsightsIcon from '@mui/icons-material/Insights';
import DatasetIcon from '@mui/icons-material/Dataset';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import CancelIcon from '@mui/icons-material/Cancel';
import MapIcon from '@mui/icons-material/Map';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DesignEditPane from '../components/DesignEditPane';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useMsal } from '@azure/msal-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

// Tab Panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`transaction-tabpanel-${index}`}
      aria-labelledby={`transaction-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Helper function to copy image using fallback method
const copyImageFallback = async (imagebase64, convertedImage) => {
  try {
    if (imagebase64) {
      // Convert base64 to blob and copy as image
      const base64Data = imagebase64.startsWith('data:')
        ? imagebase64
        : `data:image/jpeg;base64,${imagebase64}`;

      // Extract base64 string and mime type
      const matches = base64Data.match(/^data:([^;]+);base64,(.+)$/);
      if (matches) {
        const mimeType = matches[1];
        const base64String = matches[2];

        // Convert base64 to binary
        const byteCharacters = atob(base64String);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });

        // Copy image to clipboard
        await navigator.clipboard.write([
          new ClipboardItem({
            [blob.type]: blob
          })
        ]);
        return;
      }
    }

    if (convertedImage) {
      // Fetch image from URL and copy
      const response = await fetch(convertedImage);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({
          [blob.type]: blob
        })
      ]);
      return;
    }

    throw new Error('No image data available');
  } catch (e) {
    console.error('Fallback copy failed:', e);
    throw e;
  }
};

const pagePalette = {
  background: "#F5F7FB",
  surface: "#FFFFFF",
  ink: "#172033",
  muted: "#667085",
  border: "#DDE7F0",
  primary: "#22577A",
  primaryLight: "#E7F4FF",
  accent: "#38A3A5",
  warning: "#F59E0B",
  danger: "#E5484D",
  success: "#2E7D59",
};

const formatProcessingDuration = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return "N/A";
  const total = Math.max(0, Math.round(Number(seconds)));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const remainingSeconds = total % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${remainingSeconds}s`;
  if (minutes > 0) return `${minutes}m ${remainingSeconds}s`;
  return `${remainingSeconds}s`;
};

const primaryButtonSx = {
  bgcolor: pagePalette.primary,
  color: "#fff",
  fontWeight: 700,
  borderRadius: 1.5,
  px: 2,
  textTransform: "none",
  boxShadow: "0 8px 18px rgba(34, 87, 122, 0.18)",
  "&:hover": { bgcolor: "#1B4965", boxShadow: "0 10px 22px rgba(34, 87, 122, 0.22)" },
  "&:disabled": { backgroundColor: "#D4DEE8", color: "#7D8B99", boxShadow: "none" },
};

const secondaryButtonSx = {
  color: pagePalette.primary,
  borderColor: "#BBD7EA",
  bgcolor: "#fff",
  fontWeight: 700,
  borderRadius: 1.5,
  px: 2,
  textTransform: "none",
  "&:hover": { borderColor: pagePalette.primary, bgcolor: pagePalette.primaryLight },
};

const detailIconButtonSx = {
  bgcolor: pagePalette.primary,
  color: "#fff",
  borderRadius: 1.5,
  boxShadow: "0 8px 18px rgba(34, 87, 122, 0.18)",
  "&:hover": { bgcolor: "#1B4965" },
};

const detailCardSx = {
  borderRadius: 2,
  border: `1px solid ${pagePalette.border}`,
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
  overflow: "hidden",
  "& .MuiCardContent-root": {
    p: 2,
  },
  "& .MuiCardContent-root:last-child": {
    pb: 2,
  },
};

function MetricCard({ icon, label, value, color = pagePalette.primary, helper }) {
  return (
    <Card sx={{
      minWidth: 210,
      flex: 1,
      borderRadius: 2,
      border: `1px solid ${pagePalette.border}`,
      boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
      background: pagePalette.surface,
      overflow: "hidden",
    }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Box>
            <Typography sx={{ color: pagePalette.muted, fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0 }}>
              {label}
            </Typography>
            <Typography sx={{ color: pagePalette.ink, fontSize: "1.75rem", lineHeight: 1.2, fontWeight: 800, mt: 0.5 }}>
              {value}
            </Typography>
            {helper && (
              <Typography sx={{ color: pagePalette.muted, fontSize: "0.78rem", mt: 0.5 }}>
                {helper}
              </Typography>
            )}
          </Box>
          <Box sx={{
            width: 42,
            height: 42,
            borderRadius: 1.5,
            display: "grid",
            placeItems: "center",
            bgcolor: `${color}18`,
            color,
          }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

const getStatusChipSx = (status) => {
  const normalized = String(status || "").toLowerCase();
  // Must precede the generic "complete" branch below, which would paint this green.
  if (normalized.includes("attention")) {
    return { bgcolor: "#FFF8E1", color: "#8D6E00", borderColor: "#F2D67A" };
  }
  if (normalized.includes("success") || normalized.includes("complete") || normalized.includes("processed")) {
    return { bgcolor: "#E8F7EF", color: pagePalette.success, borderColor: "#B9E5CC" };
  }
  if (normalized.includes("fail") || normalized.includes("error")) {
    return { bgcolor: "#FDECEC", color: pagePalette.danger, borderColor: "#FAC5C7" };
  }
  if (normalized.includes("pending") || normalized.includes("progress")) {
    return { bgcolor: "#FFF7E8", color: "#A15C00", borderColor: "#F9DCA6" };
  }
  return { bgcolor: "#EEF6FF", color: pagePalette.primary, borderColor: "#CFE2F3" };
};

const getLatestDesignOutputRecord = async (addressId, rowId) => {
  if (!addressId && !rowId) {
    return null;
  }

  const params = new URLSearchParams();
  if (rowId) {
    params.append('row_id', rowId);
  } else if (addressId) {
    params.append('address_id', addressId);
  }

  const response = await fetch(
    `${backendRoot}/api/design-output/?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  let records = [];

  if (Array.isArray(data)) {
    records = data;
  } else if (data?.results && Array.isArray(data.results)) {
    records = data.results;
  } else if (data && typeof data === "object") {
    records = [data];
  }

  if (rowId) {
    return records[0] || null;
  }

  const filteredRecords = records.filter((item) =>
    item.address_id === addressId ||
    item.address_id === String(addressId)
  );

  if (filteredRecords.length === 0) {
    return null;
  }

  return filteredRecords.sort((a, b) => (b.row_id || 0) - (a.row_id || 0))[0];
};

const formatIndicatorValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return String(value);
};

const parseMaybeJson = (value) => {
  if (!value || typeof value !== "string") {
    return value || null;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const collectMatchingValues = (source, keyMatcher) => {
  const values = [];
  const seen = new Set();

  const visit = (node) => {
    if (node === null || node === undefined) return;

    const parsedNode = parseMaybeJson(node);
    if (parsedNode && typeof parsedNode === "object") {
      if (seen.has(parsedNode)) return;
      seen.add(parsedNode);

      if (Array.isArray(parsedNode)) {
        parsedNode.forEach(visit);
        return;
      }

      Object.entries(parsedNode).forEach(([key, value]) => {
        if (keyMatcher(key)) {
          values.push(value);
        }
        visit(value);
      });
    }
  };

  visit(source);
  return values;
};

const normalizeObjectKey = (key) => key.toLowerCase().replace(/[^a-z0-9]/g, "");

const collectValuesForKeys = (sources, keys) => (
  sources.flatMap((source) => collectMatchingValues(source, (key) => (
    keys.includes(normalizeObjectKey(key))
  )))
);

const normalizePlacementDropValue = (value, fallback = "N/A") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (typeof value === "boolean") {
    return value ? "Aerial" : "Buried";
  }

  const normalized = String(value).trim().toLowerCase();
  if (normalized.includes("hybrid")) return "Hybrid";
  if (normalized.includes("aerial")) return "Aerial";
  if (normalized.includes("buried") || normalized.includes("underground")) return "Buried";
  if (["true", "yes", "y", "1"].includes(normalized)) return "Aerial";
  if (["false", "no", "n", "0"].includes(normalized)) return "Buried";
  return fallback;
};

const tempDropLabel = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (["no", "n", "false", "0"].includes(normalized)) return "Ineligible";
  if (["yes", "y", "true", "1"].includes(normalized)) return "Eligible";
  return "N/A";
};

const normalizeTempDropValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  const normalized = String(value).trim().toLowerCase();
  if (["yes", "y", "true", "1"].includes(normalized)) return "Yes";
  if (["no", "n", "false", "0"].includes(normalized)) return "No";
  if (["n/a", "na", "not applicable"].includes(normalized)) return "N/A";
  return "N/A";
};

const firstResolvedValue = (values, normalizer, fallback = "N/A") => {
  for (const value of values) {
    const resolvedValue = normalizer(value, fallback);
    if (resolvedValue !== fallback) {
      return resolvedValue;
    }
  }
  return fallback;
};

const getTransactionSummaryValues = (transaction, designDetails, terminalDetails) => {
  const sourceRecord = transaction?.raw_source || {};
  const payload = parseMaybeJson(transaction?.payload);
  const allAddresses = parseMaybeJson(transaction?.all_addresses);
  const sources = [transaction, designDetails, terminalDetails, payload, allAddresses, sourceRecord];

  const placementValues = [
    transaction?.placement_type,
    sourceRecord?.placement_type,
    sourceRecord?.terminal_placement_type,
    ...collectValuesForKeys(sources, ["placementtype", "terminalplacementtype"]),
    designDetails?.tdp_scenario,
    transaction?.tdp_scenario,
    sourceRecord?.tdp_scenario,
    ...collectValuesForKeys(sources, ["tdpscenario", "scenario"]),
  ];

  // TDP-computed result takes priority: resolve booleans to strings here because
  // normalizePlacementDropValue cannot distinguish Hybrid from Aerial via a bare boolean.
  const tdpDropType =
    designDetails?.tdp_hybrid === true || transaction?.tdp_hybrid === true ? "Hybrid"
    : designDetails?.tdp_aerial === true || transaction?.tdp_aerial === true ? "Aerial"
    : designDetails?.tdp_aerial === false || transaction?.tdp_aerial === false ? "Buried"
    : undefined;

  const dropValues = [
    tdpDropType,
    transaction?.drop_type,
    sourceRecord?.drop_type,
    sourceRecord?.address_drop_type,
    sourceRecord?.irm_drop_type,
    ...collectValuesForKeys(sources, ["droptype", "addressdroptype", "irmdroptype"]),
    designDetails?.tdp_scenario,
    transaction?.tdp_scenario,
    sourceRecord?.tdp_scenario,
    ...collectValuesForKeys(sources, ["tdpscenario", "scenario"]),
  ];

  const tempDropValues = [
    designDetails?.tdp_temp_drop,
    transaction?.tdp_temp_drop,
    transaction?.temp_drop,
    sourceRecord?.temp_drop,
    sourceRecord?.tdp_temp_drop,
    ...collectValuesForKeys(sources, ["tempdrop", "tdptempdrop"]),
  ];

  return {
    placementType: firstResolvedValue(placementValues, normalizePlacementDropValue),
    dropType: firstResolvedValue(dropValues, normalizePlacementDropValue),
    tempDrop: firstResolvedValue(tempDropValues, normalizeTempDropValue),
  };
};

const bswDesignStatus = (transaction, placementType = transaction?.placement_type, dropType = transaction?.drop_type) => {
  if (transaction?.processing_mode === 'tdp_only') return 'Not requested';
  if (String(placementType || '').toLowerCase() === 'aerial' &&
      String(dropType || '').toLowerCase() === 'aerial') return 'Not Applicable';
  const status = String(transaction?.processing_status || '').toLowerCase();
  if (status === 'completed' || status === 'sent to kafka') return 'Ready';
  if (status === 'processing') return 'Processing';
  if (status === 'failed' || status === 'error') return 'Failed';
  if (['pending', 'queued', 'inserted'].includes(status)) return 'Pending';
  return 'N/A';
};

const hasAnyTdpSummary = (record) => {
  if (!record) return false;
  return (
    (record.tdp_temp_drop !== null && record.tdp_temp_drop !== undefined && record.tdp_temp_drop !== "") ||
    (record.tdp_scenario !== null && record.tdp_scenario !== undefined && record.tdp_scenario !== "") ||
    (record.tdp_due_date_interval !== null && record.tdp_due_date_interval !== undefined && record.tdp_due_date_interval !== "") ||
    record.tdp_aerial !== null && record.tdp_aerial !== undefined ||
    record.tdp_hybrid !== null && record.tdp_hybrid !== undefined
  );
};

// TDP status is independent of overall/design status. If TDP produced any
// result field, TDP ran and we report it as Completed.
const getTdpStatus = (details) => {
  if (!details) return "N/A";
  return hasAnyTdpSummary(details) ? "Completed" : "N/A";
};

const isBrowserSafeImageUrl = (url) => {
  if (!url) return false;
  return !url.includes("truestreetimage.com") && !url.includes("/api/generate-image/");
};

const getSignedOrRawImageUrl = (payload) => {
  if (Array.isArray(payload)) payload = payload[0];
  if (isBrowserSafeImageUrl(payload?.signed_url)) return payload.signed_url;
  if (isBrowserSafeImageUrl(payload?.url)) return payload.url;
  return null;
};

const getPrimaryStreetViewAddress = (transaction, terminalDetails) => {
  const candidates = [
    transaction?.full_address,
    transaction?.serving_address,
    transaction?.address,
    transaction?.address_string,
    transaction?.service_address,
    terminalDetails?.addr1,
  ];

  return candidates.find((value) => String(value || "").trim()) || "";
};

const getPrimaryStreetViewCoordinates = (transaction, terminalDetails) => {
  const coordinateCandidates = [
    [transaction?.building?.latitude, transaction?.building?.longitude],
    [transaction?.terminal?.latitude, transaction?.terminal?.longitude],
    [terminalDetails?.addr1_lat, terminalDetails?.addr1_long],
  ];

  for (const [lat, lng] of coordinateCandidates) {
    const latitude = Number(lat);
    const longitude = Number(lng);
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      return { latitude, longitude };
    }
  }

  return null;
};

const buildGoogleStreetViewUrls = (transaction, terminalDetails) => {
  const address = getPrimaryStreetViewAddress(transaction, terminalDetails);
  const coordinates = getPrimaryStreetViewCoordinates(transaction, terminalDetails);

  if (coordinates) {
    const { latitude, longitude } = coordinates;
    return {
      address,
      embedUrl: `https://maps.google.com/maps?layer=c&cbll=${latitude},${longitude}&cbp=11,0,0,0,0&output=svembed`,
      mapUrl: `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${latitude},${longitude}`,
    };
  }

  if (!address) {
    return { address: "", embedUrl: "", mapUrl: "" };
  }

  const encodedAddress = encodeURIComponent(address);
  return {
    address,
    embedUrl: `https://maps.google.com/maps?q=${encodedAddress}&layer=c&output=svembed`,
    mapUrl: `https://www.google.com/maps?q=${encodedAddress}&layer=c`,
  };
};

const toFiniteCoordinate = (lat, lng) => {
  const latitude = Number(lat);
  const longitude = Number(lng);
  if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
    return { latitude, longitude };
  }
  return null;
};

// Terminal coordinates live in the payload as geometry.coordinates = [lng, lat]
const getTerminalCoordinatesFromPayload = (payloadLike) => {
  const payload = parseMaybeJson(payloadLike);
  const coordinates = payload?.terminalDetails?.geometry?.coordinates;
  const terminal = toFiniteCoordinate(coordinates?.[1], coordinates?.[0]);
  return { lat: terminal?.latitude ?? null, lng: terminal?.longitude ?? null };
};

// Home + terminal coordinates for the Drop Markers map, sourced from the
// design-input payload (addresses[0].address and terminalDetails.geometry)
const getDropMarkerCoordinates = (transaction, terminalDetails) => {
  const payload = parseMaybeJson(transaction?.payload);

  const homeAddress = payload?.addresses?.[0]?.address;
  const home =
    toFiniteCoordinate(homeAddress?.latitude, homeAddress?.longitude) ||
    toFiniteCoordinate(terminalDetails?.addr1_lat, terminalDetails?.addr1_long) ||
    toFiniteCoordinate(transaction?.building?.latitude, transaction?.building?.longitude);

  const terminalCoordinates = payload?.terminalDetails?.geometry?.coordinates;
  const terminal =
    toFiniteCoordinate(terminalCoordinates?.[1], terminalCoordinates?.[0]) ||
    toFiniteCoordinate(terminalDetails?.terminal_lat, terminalDetails?.terminal_long) ||
    toFiniteCoordinate(transaction?.terminal?.latitude, transaction?.terminal?.longitude);

  return { home, terminal };
};

// Keyless Google Maps embed showing the terminal and home address pins.
// Without the Maps JavaScript API (which needs an API key), custom yellow/red
// markers cannot be drawn; the embed shows both points as start/end pins on
// the satellite layer instead.
const buildDropMarkersUrls = (transaction, terminalDetails) => {
  const { home, terminal } = getDropMarkerCoordinates(transaction, terminalDetails);

  if (home && terminal) {
    const terminalPoint = `${terminal.latitude},${terminal.longitude}`;
    const homePoint = `${home.latitude},${home.longitude}`;
    return {
      home,
      terminal,
      embedUrl: `https://maps.google.com/maps?saddr=${terminalPoint}&daddr=${homePoint}&t=k&z=19&output=embed`,
      mapUrl: `https://www.google.com/maps/dir/?api=1&origin=${terminalPoint}&destination=${homePoint}&travelmode=walking`,
    };
  }

  const onlyPoint = home || terminal;
  if (onlyPoint) {
    const point = `${onlyPoint.latitude},${onlyPoint.longitude}`;
    return {
      home,
      terminal,
      embedUrl: `https://maps.google.com/maps?q=${point}&t=k&z=19&output=embed`,
      mapUrl: `https://www.google.com/maps?q=${point}&t=k`,
    };
  }

  return { home: null, terminal: null, embedUrl: "", mapUrl: "" };
};

// Keyless street view embed + open-in-maps links for a single coordinate
const buildStreetViewLinksForPoint = (point) => {
  if (!point) return { embedUrl: "", mapUrl: "" };
  const { latitude, longitude } = point;
  return {
    embedUrl: `https://maps.google.com/maps?layer=c&cbll=${latitude},${longitude}&cbp=11,0,0,0,0&output=svembed`,
    mapUrl: `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${latitude},${longitude}`,
  };
};

const TERMINAL_MARKER_ICON = L.divIcon({
  className: '',
  html: "<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20'><circle cx='10' cy='10' r='7' fill='#FFD600' stroke='#000' stroke-width='2'/></svg>",
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const HOME_MARKER_ICON = L.divIcon({
  className: '',
  html: "<svg xmlns='http://www.w3.org/2000/svg' width='30' height='42' viewBox='0 0 30 42'><path d='M15 0C6.7 0 0 6.7 0 15c0 11.2 15 27 15 27s15-15.8 15-27C30 6.7 23.3 0 15 0z' fill='#EA4335'/><circle cx='15' cy='15' r='6' fill='#7B231E'/></svg>",
  iconSize: [30, 42],
  iconAnchor: [15, 42],
});

// Satellite map with the terminal (yellow dot) and home (red pin) pointers and
// no route overlay. Leaflet + Esri World Imagery needs no API key. Clicking a
// pointer reports it via onMarkerClick so the panel can switch to Street View.
function DropMarkersLeafletMap({ homeLat, homeLng, terminalLat, terminalLng, onMarkerClick }) {
  const containerRef = useRef(null);
  const onMarkerClickRef = useRef(onMarkerClick);
  onMarkerClickRef.current = onMarkerClick;

  useEffect(() => {
    const home = toFiniteCoordinate(homeLat, homeLng);
    const terminal = toFiniteCoordinate(terminalLat, terminalLng);
    if (!containerRef.current || (!home && !terminal)) return undefined;

    const map = L.map(containerRef.current);
    L.tileLayer(
      'http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
      { maxZoom: 21, subdomains: ['mt0', 'mt1', 'mt2', 'mt3'], attribution: 'Google Satellite' }
    ).addTo(map);

    if (terminal) {
      L.marker([terminal.latitude, terminal.longitude], { title: 'Terminal', icon: TERMINAL_MARKER_ICON })
        .addTo(map)
        .bindTooltip('Terminal — click for Street View')
        .on('click', () => onMarkerClickRef.current?.('terminal'));
    }

    if (home) {
      L.marker([home.latitude, home.longitude], { title: 'Home', icon: HOME_MARKER_ICON })
        .addTo(map)
        .bindTooltip('Home — click for Street View')
        .on('click', () => onMarkerClickRef.current?.('home'));
    }

    if (home && terminal) {
      map.fitBounds(
        [
          [terminal.latitude, terminal.longitude],
          [home.latitude, home.longitude],
        ],
        { padding: [60, 60], maxZoom: 19 }
      );
    } else {
      const only = home || terminal;
      map.setView([only.latitude, only.longitude], 18);
    }

    return () => {
      map.remove();
    };
  }, [homeLat, homeLng, terminalLat, terminalLng]);

  return <div ref={containerRef} style={{ height: '100%', width: '100%' }} />;
}

const ROLE_LABELS = {
  building_streetview: 'Building Street View',
  streetview_front: 'Street View (Front)',
  streetview_front_candidate: 'Street View (Front Candidate)',
  streetview_other_side: 'Street View (Other Side)',
  premise_pole_streetview: 'Premise Pole Street View',
  serving_terminal_streetview: 'Serving Terminal Street View',
  maps: 'Maps',
  satellite: 'Satellite',
  hybrid_review: 'Hybrid Review',
  map: 'Map',
  converted_image: 'Converted Image',
};

const STREETVIEW_DISPLAY_ROLES = new Set([
  'building_streetview',
  'streetview_front',
  'streetview_front_candidate',
  'streetview_other_side',
  'premise_pole_streetview',
  'serving_terminal_streetview',
]);

// Resolve processed image URLs for an address.
// Always uses /api/processed-images/ to get a signed GCS URL (30-min TTL).
// The raw GCS URL in tdp_raw is a private-bucket URL and cannot be loaded
// directly in an <img> tag without a signed URL.
const resolveProcessedImages = async (addressId, rowId, _record) => {
  const payload = await resolveProcessedPayload(addressId, rowId);
  return payload?.images || null;
};

const resolveProcessedPayload = async (addressId, rowId) => {
  if (!addressId && !rowId) return null;
  try {
    const params = new URLSearchParams();
    if (rowId) {
      params.append("row_id", rowId);
    } else if (addressId) {
      params.append("address_id", addressId);
    }
    const response = await fetch(
      `${backendRoot}/api/processed-images/?${params.toString()}`,
      { method: "GET", headers: { "Content-Type": "application/json" } }
    );
    if (!response.ok) return null;
    const data = await response.json();
    return data || null;
  } catch (error) {
    console.error("Error fetching processed images:", error);
    return null;
  }
};

const fetchDrawerJson = async (path, signal) => {
  const response = await fetch(`${backendRoot}${path}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    signal,
  });
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }
  return response.json();
};

const drawerDetailKey = (rowId) => ["transaction-drawer", "detail", Number(rowId)];
const drawerMediaKey = (rowId) => ["transaction-drawer", "media", Number(rowId)];
const drawerPayloadKey = (rowId) => ["transaction-drawer", "payload", Number(rowId)];

const drawerDetailQueryOptions = (rowId) => ({
  queryKey: drawerDetailKey(rowId),
  queryFn: ({ signal }) => fetchDrawerJson(`/api/transaction-drawer/${encodeURIComponent(rowId)}/`, signal),
  staleTime: 5 * 60 * 1000,
  gcTime: 15 * 60 * 1000,
});

const drawerMediaQueryOptions = (rowId) => ({
  queryKey: drawerMediaKey(rowId),
  queryFn: ({ signal }) => fetchDrawerJson(`/api/transaction-drawer/${encodeURIComponent(rowId)}/media/`, signal),
  staleTime: 20 * 60 * 1000,
  gcTime: 30 * 60 * 1000,
});

const drawerPayloadQueryOptions = (rowId) => ({
  queryKey: drawerPayloadKey(rowId),
  queryFn: ({ signal }) => fetchDrawerJson(`/api/design-input/${encodeURIComponent(rowId)}/`, signal),
  staleTime: 5 * 60 * 1000,
  gcTime: 15 * 60 * 1000,
});
const mergeProcessedTdp = (details, processedPayload) => {
  const tdp = processedPayload?.tdp || {};
  return {
    ...details,
    tdp_temp_drop: tdp.temp_drop ?? details.tdp_temp_drop ?? null,
    tdp_aerial: tdp.aerial ?? details.tdp_aerial ?? null,
    tdp_hybrid: tdp.hybrid ?? details.tdp_hybrid ?? null,
    tdp_scenario: tdp.scenario ?? details.tdp_scenario ?? null,
    tdp_due_date_interval: tdp.due_date_interval ?? details.tdp_due_date_interval ?? null,
  };
};

const noteOptions = ["", "Incorrect payload", "Yes", "No", "Partial"];

export default function Transactions() {
  const { accounts } = useMsal();
  const auditorName = accounts?.[0]?.name || accounts?.[0]?.username || 'Unknown';

  // State for transactions data from backend
  const [transactions, setTransactions] = useState([]);

  const [loading, setLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const isClientUser = !!localStorage.getItem('clientToken');
  const [transactionsPerPage, setTransactionsPerPage] = useState(100); // Rows per page (server-side)

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = React.useRef(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [designEditOpen, setDesignEditOpen] = useState(false);
  // Google Street Map panel view: 'map' (satellite + pointers) | 'home' | 'terminal' (street view)
  const [streetMapMode, setStreetMapMode] = useState('map');
  const [auditSelections, setAuditSelections] = useState({});
  const [commentSelections, setCommentSelections] = useState({});
  const [submittedComments, setSubmittedComments] = useState({});
  const [auditMeta, setAuditMeta] = useState({});
  const [commentWarning, setCommentWarning] = useState('');
  const [transactionDesignDetails, setTransactionDesignDetails] = useState(null);
  const [processedStreetViewUrl, setProcessedStreetViewUrl] = useState(null);
  const [hybridReviewImageUrl, setHybridReviewImageUrl] = useState(null);
  const [allProcessedImages, setAllProcessedImages] = useState(null);
  const [terminalAddressDetails, setTerminalAddressDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [tempDropFilter, setTempDropFilter] = useState('');
  const [hybridFilter, setHybridFilter] = useState('');
  const [aerialFilter, setAerialFilter] = useState('');
  const [scenarioFilter, setScenarioFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryMetrics, setSummaryMetrics] = useState({
    totalInputCount: 0,
    totalTickets: 0,
    totalFailed: 0,
    failedInDateRange: 0,
    successRate: 0
  });
  const [submittingTransactions, setSubmittingTransactions] = useState(new Set());
  const [transactionToProcess, setTransactionToProcess] = useState(null);
  const [transactionProcessingMode, setTransactionProcessingMode] = useState('tdp_bsw');
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [imagePreviewSrc, setImagePreviewSrc] = useState(null);
  const [notesMap, setNotesMap] = useState({});
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentSaved, setCommentSaved] = useState(false);
  const [savedNoteMap, setSavedNoteMap] = useState({});
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterOpen, setFilterOpen] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    count: 0,
    page: 1,
    page_size: 50,
    total_pages: 0
  });
  const designImageRef = useRef(null);
  const transactionTableContainerRef = useRef(null);
  const drawerPrefetchTimerRef = useRef(null);
  const activeDetailsRowIdRef = useRef(null);
  const transactionRequestIdRef = useRef(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // When opened from a project row, scope every fetch to that project.
  const projectId = searchParams.get("project_id") || "";

  const queryClient = useQueryClient();
  const selectedRowId = selectedTransaction?.row_id ?? selectedTransaction?.id ?? null;
  const drawerDetailQuery = useQuery({
    ...drawerDetailQueryOptions(selectedRowId),
    enabled: rightPanelOpen && Boolean(selectedRowId),
  });
  const drawerMediaQuery = useQuery({
    ...drawerMediaQueryOptions(selectedRowId),
    enabled: rightPanelOpen && Boolean(selectedRowId) && (activeTab === 0 || activeTab === 1),
  });
  const drawerPayloadQuery = useQuery({
    ...drawerPayloadQueryOptions(selectedRowId),
    enabled: rightPanelOpen && Boolean(selectedRowId) && activeTab === 3,
  });

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
      setPage(1);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [searchQuery]);

  const searchPending = searchQuery.trim() !== debouncedSearchQuery;
  useEffect(() => {
    const data = drawerDetailQuery.data;
    if (!data || Number(data.row_id) !== Number(selectedRowId)) return;

    const transaction = data.transaction || {};
    const design = data.design || {};
    setSelectedTransaction((previous) => {
      if (!previous || Number(previous.row_id ?? previous.id) !== Number(data.row_id)) return previous;
      return {
        ...previous,
        ticket_id: transaction.ticket_id ?? previous.ticket_id,
        address_id: transaction.address_id ?? previous.address_id,
        full_address: transaction.full_address ?? previous.full_address,
        processing_status: transaction.processing_status ?? previous.processing_status,
        processing_mode: transaction.processing_mode ?? previous.processing_mode,
        placement_type: transaction.placement_type ?? previous.placement_type,
        terminal_placement_type: transaction.placement_type ?? previous.terminal_placement_type,
        drop_type: transaction.drop_type ?? previous.drop_type,
        drop_classifier_result: transaction.drop_classifier_result ?? previous.drop_classifier_result,
        drop_classifier_status: transaction.drop_classifier_status ?? previous.drop_classifier_status,
        drop_classifier_error: transaction.drop_classifier_error ?? previous.drop_classifier_error,
        drop_classifier_raw: {
          ...(previous.drop_classifier_raw || {}),
          confidence: transaction.drop_classifier_confidence ?? previous.drop_classifier_raw?.confidence,
          evidence: transaction.drop_classifier_evidence ?? previous.drop_classifier_raw?.evidence,
        },
      };
    });
    setTransactionDesignDetails((previous) => ({
      ...design,
      tdp_raw: design.tdp_message ? { message: design.tdp_message } : null,
      imagebase64: previous?.imagebase64 ?? null,
      converted_image: previous?.converted_image || null,
    }));
    setTerminalAddressDetails(data.terminal_address_details || null);

    const audit = data.audit;
    const txId = selectedTransaction?.id;
    if (audit && txId) {
      setAuditSelections((previous) => ({
        ...previous,
        [txId]: {
          tempDrop: audit.temp_drop_audit || null,
          tdpIndicator: audit.tdp_indicator_audit || null,
          bswDesign: audit.bsw_design_audit || null,
          dropType: audit.drop_type_audit || null,
        },
      }));
      setSubmittedComments((previous) => ({
        ...previous,
        [txId]: {
          tempDrop: audit.temp_drop_comment || null,
          bswDesign: audit.bsw_design_comment || null,
        },
      }));
      if (audit.audited_by || audit.audited_at) {
        setAuditMeta((previous) => ({
          ...previous,
          [txId]: { audited_by: audit.audited_by, audited_at: audit.audited_at },
        }));
      }
    }
  }, [drawerDetailQuery.data, selectedRowId, selectedTransaction?.id]);

  useEffect(() => {
    const data = drawerMediaQuery.data;
    if (!data || Number(data.row_id) !== Number(selectedRowId)) return;
    const designImage = data.design_image || null;
    const imageUrl = getSignedOrRawImageUrl(designImage);
    setTransactionDesignDetails((previous) => ({
      ...(previous || {}),
      is_historical: data.design_is_historical ?? previous?.is_historical ?? false,
      imagebase64: designImage?.imagebase64 || null,
      converted_image: imageUrl || previous?.converted_image || null,
    }));
    const images = data.images || null;
    setAllProcessedImages(images);
    setHybridReviewImageUrl(getSignedOrRawImageUrl(images?.hybrid_review));
    setProcessedStreetViewUrl(getSignedOrRawImageUrl(images?.building_streetview));
  }, [drawerMediaQuery.data, selectedRowId]);

  useEffect(() => {
    const data = drawerPayloadQuery.data;
    if (!data || Number(data.row_id) !== Number(selectedRowId)) return;
    setSelectedTransaction((previous) => {
      if (!previous || Number(previous.row_id ?? previous.id) !== Number(data.row_id)) return previous;
      return {
        ...previous,
        payload: data.payload ?? previous.payload ?? null,
        all_addresses: data.all_addresses ?? previous.all_addresses ?? null,
      };
    });
  }, [drawerPayloadQuery.data, selectedRowId]);

  const scheduleDrawerPrefetch = useCallback((transaction) => {
    if (!transaction || transaction.isEmpty) return;
    window.clearTimeout(drawerPrefetchTimerRef.current);
    const rowId = transaction.row_id ?? transaction.id;
    drawerPrefetchTimerRef.current = window.setTimeout(() => {
      queryClient.prefetchQuery(drawerDetailQueryOptions(rowId));
    }, 150);
  }, [queryClient]);

  const cancelDrawerPrefetch = useCallback(() => {
    window.clearTimeout(drawerPrefetchTimerRef.current);
  }, []);

  useEffect(() => () => window.clearTimeout(drawerPrefetchTimerRef.current), []);

  // Helper function to calculate time difference in hh:mm:ss format
  const calculateTimeDifference = (kafkaSentAt, receivedTs) => {
    if (!kafkaSentAt || !receivedTs) {
      return '';
    }

    try {
      const kafkaDate = new Date(kafkaSentAt);
      const receivedDate = new Date(receivedTs);

      if (isNaN(kafkaDate.getTime()) || isNaN(receivedDate.getTime())) {
        return '';
      }

      const diffMs = kafkaDate.getTime() - receivedDate.getTime();
      if (diffMs < 0) {
        return '';
      }

      const totalSeconds = Math.floor(diffMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    } catch (error) {
      console.error('Error calculating time difference:', error);
      return '';
    }
  };

  // Fetch transactions from backend API with server-side filtering and pagination
  const fetchTransactions = useCallback(async () => {
    const requestId = ++transactionRequestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      // Build query parameters with date filters, pagination, and search
      const params = new URLSearchParams();
      if (startDate) {
        params.append('start_date', startDate);
      }
      if (endDate) {
        params.append('end_date', endDate);
      }
      if (debouncedSearchQuery) {
        params.append('search', debouncedSearchQuery);
      }
      if (tempDropFilter) {
        params.append('tdp_temp_drop', tempDropFilter);
      }
      if (hybridFilter) {
        params.append('tdp_hybrid', hybridFilter);
      }
      if (aerialFilter) {
        params.append('tdp_aerial', aerialFilter);
      }
      if (scenarioFilter) {
        params.append('tdp_scenario', scenarioFilter);
      }
      if (statusFilter) {
        params.append('processing_status', statusFilter);
      }
      if (projectId) {
        params.append('project_id', projectId);
      }
      params.append('page', page.toString());
      params.append('page_size', transactionsPerPage.toString());
      params.append('sort_order', sortOrder);

      // Fetch design input data with server-side filtering
      const inputResponse = await fetch(
        `${backendRoot}/api/design-input/?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!inputResponse.ok) {
        throw new Error(`HTTP error! status: ${inputResponse.status}`);
      }

      const responseData = await inputResponse.json();
      if (requestId !== transactionRequestIdRef.current) return;
      console.log('Fetched transactions data:', responseData);

      // Handle paginated response (backward compatible - supports both array and paginated format)
      let data = [];
      if (Array.isArray(responseData)) {
        // Legacy format: array response (for backward compatibility)
        data = responseData;
        setPaginationInfo({
          count: data.length,
          page: 1,
          page_size: data.length,
          total_pages: 1
        });
      } else if (responseData.results) {
        // New paginated format
        data = responseData.results;
        const serverPage = Number(responseData.page) || page;
        setPaginationInfo({
          count: responseData.count || data.length,
          page: serverPage,
          page_size: responseData.page_size || transactionsPerPage,
          total_pages: responseData.total_pages || 1
        });
        // The server clamps out-of-range pages (e.g. rows deleted while the
        // reviewer sat on the last page). Adopt its answer so the controls and
        // the "Showing x-y" range never disagree with the rows on screen.
        if (serverPage !== page) {
          setPage(serverPage);
        }
      } else {
        // Fallback: empty array
        data = [];
        setPaginationInfo({
          count: 0,
          page: 1,
          page_size: transactionsPerPage,
          total_pages: 0
        });
      }

      // Debug: Log first few items to see what processing_status values we're getting
      if (data.length > 0) {
        console.log('=== DEBUG: Sample transactions with processing_status ===');
        data.slice(0, 10).forEach((item, idx) => {
          const rawStatus = item.processing_status;
          console.log(`Transaction ${idx + 1} (row_id: ${item.row_id}):`, {
            raw_processing_status: rawStatus,
            type: typeof rawStatus,
            is_null: rawStatus === null,
            is_undefined: rawStatus === undefined,
            is_empty_string: rawStatus === '',
            is_falsy: !rawStatus,
            has_processing_status: 'processing_status' in item,
            all_status_keys: Object.keys(item).filter(k => k.includes('status')),
            full_item_keys: Object.keys(item).slice(0, 10) // First 10 keys
          });
        });
        console.log('=== END DEBUG ===');
      }

      // Transform backend data to match frontend format
      const transformedTransactions = data.map((item, index) => {
        // Get processing_status directly from the API response
        // Only default to 'Pending' if it's actually null, undefined, or empty string
        // This ensures we show the exact value stored in the database
        let processingStatus = item.processing_status;
        if (processingStatus === null || processingStatus === undefined || processingStatus === '') {
          processingStatus = 'Pending';
        } else {
          processingStatus = String(processingStatus).trim();
        }

        // Get kafka_sent_at directly from the API response (now included in DesignInput)
        const kafkaSentAt = item.kafka_sent_at || null;
        const receivedTs = item.created;
        const receivedDate = receivedTs && !isNaN(new Date(receivedTs).getTime()) ? new Date(receivedTs) : null;

        // Calculate time difference
        const timeDifference = calculateTimeDifference(kafkaSentAt, receivedTs);

        // Get address_id for display
        const addressId = item.bsw_address_id || item.address_id || '';
        const street =
          item.serving_address ||
          item.full_address ||
          item.address ||
          item.address_string ||
          item.service_address ||
          '';
        // Append city/state/zip only when the address string doesn't already
        // contain the city (Optimum rows already embed "… SEA GIRT NJ 08750")
        const cityUp = (item.city || '').trim().toUpperCase();
        const fullAddress = cityUp && !street.toUpperCase().includes(cityUp)
          ? [street, [item.city, item.state].filter(Boolean).join(', '), item.zipcode]
              .filter(Boolean).join(', ').replace(/, (\d{5}(?:-\d{4})?)$/, ' $1')
          : street;

        return {
          id: item.row_id || index + 1,
          row_id: item.row_id, // Keep row_id for API calls
          order_number: item.bd_order_number || '',
          order_due_date: '', // Not available in DesignInput
          ticket_id: item.mywwfm_ticket_id || '',
          bsw_due_date: item.bsw_due_date ? new Date(item.bsw_due_date).toISOString().split('T')[0] : '',
          terminal_id: item.terminal_id || '',
          address_id: addressId,
          full_address: fullAddress,
          received_date_time: receivedDate ? receivedDate.toISOString().split('T')[0] : '',
          received_time: receivedDate ? receivedDate.toISOString().split('T')[1].split('.')[0] : '',
          processed_date_time: item.processed_at ? new Date(item.processed_at).toISOString().replace('T', ' ').replace('Z', '').split('.')[0] : '',
          status: item.bsw_ticket_status || 'Not Processed',
          processing_status: processingStatus, // Use the properly handled processing_status
          processing_mode: item.processing_mode || 'tdp_bsw',
          needs_attention: item.needs_attention === true,
          processing_duration_seconds: item.processing_duration_seconds ?? null,
          notes: item.notes || '', // Include notes from backend
          time_difference: timeDifference, // Time difference in hh:mm:ss format
          tdp_temp_drop: item.tdp_temp_drop ?? null,
          tdp_aerial: item.tdp_aerial ?? null,
          tdp_hybrid: item.tdp_hybrid ?? null,
          tdp_scenario: item.tdp_scenario ?? null,
          tdp_due_date_interval: item.tdp_due_date_interval ?? null,
          tdp_complexity: item.tdp_complexity ?? null,
          tdp_raw: item.tdp_raw ?? null,
          drop_classifier_result: item.drop_classifier_result ?? null,
          drop_classifier_status: item.drop_classifier_status ?? null,
          drop_classifier_error: item.drop_classifier_error ?? null,
          drop_classifier_raw: item.drop_classifier_raw ?? null,
          placement_type: item.placement_type ?? item.terminal_placement_type ?? null,
          drop_type: item.drop_type ?? item.address_drop_type ?? null,
          temp_drop: item.temp_drop ?? item.tdp_temp_drop ?? null,
          payload: item.payload ?? null,
          all_addresses: item.all_addresses ?? null,
          raw_source: item,
          tdp_indicator: formatIndicatorValue(item.tdp_temp_drop),
          hybrid_indicator: formatIndicatorValue(item.tdp_hybrid),
        };
      });

      // The list endpoint already returns the row-scoped TDP and classifier
      // fields needed by the queue. Render those rows immediately instead of
      // blocking on one additional DesignOutput request per transaction.
      const filteredTransactions = transformedTransactions;

      // Each page replaces the table contents so the browser only holds one
      // page of rows at a time. Accumulating every scrolled page is what made
      // large projects (e.g. Broken Arrow) hang the tab after a few hundred rows.
      setTransactions(Array.from(
        new Map(filteredTransactions.map(transaction => [transaction.id, transaction])).values()
      ));

      // Bulk load audit state for all transactions on this page so table icons persist across refresh
      const designInputIds = filteredTransactions
        .map(t => t.design_input_id ?? t.row_id ?? t.id)
        .filter(Boolean);
      if (designInputIds.length > 0) {
        try {
          const auditRes = await fetch(`${backendRoot}/api/transaction-audit/bulk/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ design_input_ids: designInputIds }),
          });
          if (auditRes.ok) {
            const auditData = await auditRes.json();
            const newAuditSelections = {};
            filteredTransactions.forEach(t => {
              const did = t.design_input_id ?? t.row_id ?? t.id;
              const a = auditData[did];
              if (a && (a.temp_drop_audit || a.tdp_indicator_audit || a.bsw_design_audit || a.drop_type_audit)) {
                newAuditSelections[t.id] = {
                  tempDrop: a.temp_drop_audit || null,
                  tdpIndicator: a.tdp_indicator_audit || null,
                  bswDesign: a.bsw_design_audit || null,
                  dropType: a.drop_type_audit || null,
                };
              }
            });
            if (Object.keys(newAuditSelections).length > 0) {
              setAuditSelections(prev => ({ ...prev, ...newAuditSelections }));
            }
          }
        } catch (_) {}
      }

      // Populate notesMap with notes from fetched transactions (including empty strings for "None")
      const notesMapData = {};
      const savedNoteMapData = {};
      filteredTransactions.forEach(transaction => {
        // Include all notes, even if empty/null (empty string represents "None")
        notesMapData[transaction.id] = transaction.notes || '';
        savedNoteMapData[transaction.id] = transaction.notes || '';
      });
      setNotesMap(notesMapData);
      setSavedNoteMap(savedNoteMapData);
    } catch (error) {
      if (requestId !== transactionRequestIdRef.current) return;
      console.error('Error fetching transactions:', error);
      setError(`Failed to fetch transactions: ${error.message}`);

      setTransactions([]);
    } finally {
      if (requestId === transactionRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [startDate, endDate, debouncedSearchQuery, tempDropFilter, hybridFilter, aerialFilter, scenarioFilter, statusFilter, page, transactionsPerPage, sortOrder, projectId]);

  // Fetch terminal address details from DesignInput all_addresses field
  const fetchTerminalAddressDetailsFromApi = async (transaction) => {
    try {
      // Fetch full record with payload and all_addresses from detail endpoint
      const detailResponse = await fetch(
        `${backendRoot}/api/design-input/${transaction.row_id}/`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!detailResponse.ok) {
        throw new Error(`HTTP error! status: ${detailResponse.status}`);
      }

      const designInputData = await detailResponse.json();

      // Terminal coordinates come from the payload (terminalDetails.geometry)
      const payloadObj = parseMaybeJson(designInputData?.payload ?? transaction.payload);
      const terminalCoords = getTerminalCoordinatesFromPayload(payloadObj);

      if (designInputData && (designInputData.all_addresses || payloadObj?.addresses)) {
        // Parse the all_addresses JSON string, falling back to payload addresses
        const addresses = (typeof designInputData.all_addresses === 'string'
          ? JSON.parse(designInputData.all_addresses)
          : designInputData.all_addresses) || payloadObj?.addresses || [];

        // Convert addresses to terminal address details format
        const terminalAddressDetails = {
          row_num: transaction.id,
          ticket_id: transaction.ticket_id,
          terminal_id: transaction.terminal_id,
          address_id: transaction.address_id, // Add address ID
          terminal_lat: terminalCoords.lat,
          terminal_long: terminalCoords.lng,
          addr1: '',
          addr1_lat: null,
          addr1_long: null,
          addr2: '',
          addr2_lat: null,
          addr2_long: null,
          addr3: '',
          addr3_lat: null,
          addr3_long: null,
          addr4: '',
          addr4_lat: null,
          addr4_long: null,
          addr5: '',
          addr5_lat: null,
          addr5_long: null,
          addr6: '',
          addr6_lat: null,
          addr6_long: null,
          addr7: '',
          addr7_lat: null,
          addr7_long: null,
          addr8: '',
          addr8_lat: null,
          addr8_long: null,
        };

        // Map up to 8 addresses from all_addresses
        addresses.slice(0, 8).forEach((addrWrapper, index) => {
          const addr = addrWrapper.address || addrWrapper;
          const addrKey = `addr${index + 1}`;
          const latKey = `addr${index + 1}_lat`;
          const longKey = `addr${index + 1}_long`;

          terminalAddressDetails[addrKey] = addr.address_string || '';
          terminalAddressDetails[latKey] = addr.latitude ? Number(addr.latitude) : null;
          terminalAddressDetails[longKey] = addr.longitude ? Number(addr.longitude) : null;
        });

        // Initialize remaining address slots (addr6, addr7, addr8) as empty if not filled
        for (let i = addresses.length; i < 8; i++) {
          const addrKey = `addr${i + 1}`;
          const latKey = `addr${i + 1}_lat`;
          const longKey = `addr${i + 1}_long`;

          if (!terminalAddressDetails[addrKey]) {
            terminalAddressDetails[addrKey] = '';
            terminalAddressDetails[latKey] = null;
            terminalAddressDetails[longKey] = null;
          }
        }

        setTerminalAddressDetails(terminalAddressDetails);
      } else {
        // Fallback minimal details
        setTerminalAddressDetails({
          row_num: transaction.id,
          ticket_id: transaction.ticket_id,
          terminal_id: transaction.terminal_id,
          address_id: transaction.address_id,
          terminal_lat: terminalCoords.lat,
          terminal_long: terminalCoords.lng,
          addr1: transaction.full_address,
          addr1_lat: null,
          addr1_long: null,
          addr2: '',
          addr2_lat: null,
          addr2_long: null,
          addr3: '',
          addr3_lat: null,
          addr3_long: null,
          addr4: '',
          addr4_lat: null,
          addr4_long: null,
          addr5: '',
          addr5_lat: null,
          addr5_long: null,
          addr6: '',
          addr6_lat: null,
          addr6_long: null,
          addr7: '',
          addr7_lat: null,
          addr7_long: null,
          addr8: '',
          addr8_lat: null,
          addr8_long: null,
        });
      }
    } catch (err) {
      console.error('Error processing terminal address details:', err);
      const fallbackTerminalCoords = getTerminalCoordinatesFromPayload(transaction.payload);
      // Fallback minimal details
      setTerminalAddressDetails({
        row_num: transaction.id,
        ticket_id: transaction.ticket_id,
        terminal_id: transaction.terminal_id,
        address_id: transaction.address_id,
        terminal_lat: fallbackTerminalCoords.lat,
        terminal_long: fallbackTerminalCoords.lng,
        addr1: transaction.full_address,
        addr1_lat: null,
        addr1_long: null,
        addr2: '',
        addr2_lat: null,
        addr2_long: null,
        addr3: '',
        addr3_lat: null,
        addr3_long: null,
        addr4: '',
        addr4_lat: null,
        addr4_long: null,
        addr5: '',
        addr5_lat: null,
        addr5_long: null,
        addr6: '',
        addr6_lat: null,
        addr6_long: null,
        addr7: '',
        addr7_lat: null,
        addr7_long: null,
        addr8: '',
        addr8_lat: null,
        addr8_long: null,
      });
    }
  };

  // Fetch current design details for a given transaction (from stored design_output)
  const fetchCurrentDesignDetailsFromApi = async (transaction) => {
    const requestRowId = transaction.row_id || transaction.id;
    setDetailsLoading(true);
    try {
      const record = await getLatestDesignOutputRecord(transaction.address_id, transaction.row_id);
      const processedPayload = await resolveProcessedPayload(transaction.address_id, transaction.row_id);
      if (activeDetailsRowIdRef.current !== requestRowId) {
        return null;
      }

      if (record) {
        const details = mergeProcessedTdp({
          row_id: record.row_id || null,
          address_id: record.address_id || transaction.address_id,
          status: record.status || null,
          cost: record.cost || null,
          flowerpots: record.flowerpots || null,
          fp_id: record.fp_id || null,
          map_url: record.map_url || null,
          building_id: record.building_id || null,
          converted_image: record.converted_image || null,
          imagebase64: record.imagebase64 || null,
          version_id: record.version_id || null,
          is_current: record.is_current !== undefined ? record.is_current : null,
          summary_date: record.summary_date || null,
          tdp_temp_drop: record.tdp_temp_drop ?? null,
          tdp_aerial: record.tdp_aerial ?? null,
          tdp_hybrid: record.tdp_hybrid ?? null,
          tdp_scenario: record.tdp_scenario ?? null,
          tdp_due_date_interval: record.tdp_due_date_interval ?? null,
          tdp_complexity: record.tdp_complexity ?? null,
          tdp_raw: record.tdp_raw ?? null,
        }, processedPayload);
        setTransactionDesignDetails(details);
        // Resolve review/processed Street View images independently of overall status.
        const images = processedPayload?.images || null;
        setAllProcessedImages(images);
        setHybridReviewImageUrl(getSignedOrRawImageUrl(images?.hybrid_review));
        setProcessedStreetViewUrl(getSignedOrRawImageUrl(images?.building_streetview));
        setTransactions((prev) => prev.map((item) => (
          item.row_id === transaction.row_id
            ? {
                ...item,
                tdp_indicator: formatIndicatorValue(details.tdp_temp_drop),
                hybrid_indicator: formatIndicatorValue(details.tdp_hybrid),
              }
            : item
        )));
        return record;
      } else {
        console.log(`No design output found for address_id ${transaction.address_id}`);
        setTransactions((prev) => prev.map((item) => (
          item.row_id === transaction.row_id
            ? {
                ...item,
                tdp_indicator: formatIndicatorValue(transaction.tdp_temp_drop),
                hybrid_indicator: formatIndicatorValue(transaction.tdp_hybrid),
              }
            : item
        )));
        const images = processedPayload?.images || null;
        setAllProcessedImages(images);
        setHybridReviewImageUrl(getSignedOrRawImageUrl(images?.hybrid_review));
        setProcessedStreetViewUrl(getSignedOrRawImageUrl(images?.building_streetview));
        // Set all fields to null instead of hardcoded values
        setTransactionDesignDetails(mergeProcessedTdp({
          row_id: null,
          address_id: transaction.address_id,
          status: null,
          cost: null,
          flowerpots: null,
          fp_id: null,
          map_url: null,
          building_id: null,
          converted_image: null,
          imagebase64: null,
          version_id: null,
          is_current: null,
          summary_date: null,
          tdp_temp_drop: transaction.tdp_temp_drop ?? null,
          tdp_aerial: transaction.tdp_aerial ?? null,
          tdp_hybrid: transaction.tdp_hybrid ?? null,
          tdp_scenario: transaction.tdp_scenario ?? null,
          tdp_due_date_interval: transaction.tdp_due_date_interval ?? null,
          tdp_complexity: transaction.tdp_complexity ?? null,
          tdp_raw: transaction.tdp_raw ?? null,
        }, processedPayload));
        return null;
      }
    } catch (err) {
      if (activeDetailsRowIdRef.current !== requestRowId) {
        return null;
      }
      console.error('Error fetching current design details:', err);
      setAllProcessedImages(null);
      setHybridReviewImageUrl(null);
      setProcessedStreetViewUrl(null);
      // Set all fields to null instead of hardcoded values
      setTransactionDesignDetails({
        row_id: null,
        address_id: transaction.address_id,
        status: null,
        cost: null,
        flowerpots: null,
        fp_id: null,
        map_url: null,
        building_id: null,
        converted_image: null,
        imagebase64: null,
        version_id: null,
        is_current: null,
        summary_date: null,
        tdp_temp_drop: null,
        tdp_aerial: null,
        tdp_hybrid: null,
        tdp_scenario: null,
        tdp_due_date_interval: null,
        tdp_complexity: null,
        tdp_raw: null,
      });
      return null;
    } finally {
      if (activeDetailsRowIdRef.current === requestRowId) {
        setDetailsLoading(false);
      }
    }
  };

  // Fetch date-based summary metrics from backend API
  const fetchSummaryMetrics = useCallback(async () => {
    setSummaryLoading(true);
    try {
      // Build query parameters - include dates only if they're selected
      const params = new URLSearchParams();
      if (startDate) {
        params.append('start_date', startDate);
      }
      if (endDate) {
        params.append('end_date', endDate);
      }
      if (projectId) {
        params.append('project_id', projectId);
      }

      // Build URL with or without date parameters
      const url = `${backendRoot}/api/summary-metrics/${params.toString() ? '?' + params.toString() : ''}`;

      const response = await fetch(
        url,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Fetched summary metrics:', data);

      setSummaryMetrics({
        totalInputCount: data.total_input_count || 0,
        totalTickets: data.total_tickets || 0,
        totalFailed: data.total_failed || 0,
        failedInDateRange: data.failed_in_date_range || 0,
        successRate: data.success_rate || 0
      });
    } catch (error) {
      console.error('Error fetching summary metrics:', error);
      // Reset to default values on error
      setSummaryMetrics({
        totalInputCount: 0,
        totalTickets: 0,
        totalFailed: 0,
        failedInDateRange: 0,
        successRate: 0
      });
    } finally {
      setSummaryLoading(false);
    }
  }, [startDate, endDate, projectId]);

  // Any change here invalidates the current page offset, so the view must go
  // back to page 1 before a request is issued.
  const filterSignature = JSON.stringify([
    startDate, endDate, debouncedSearchQuery, tempDropFilter, hybridFilter,
    aerialFilter, scenarioFilter, statusFilter, sortOrder, projectId, transactionsPerPage,
  ]);
  const prevFilterSignature = useRef(filterSignature);

  const handlePageChange = (_event, nextPage) => {
    if (nextPage === page) return;
    setPage(nextPage);
  };

  const handleRowsPerPageChange = (event) => {
    setTransactionsPerPage(Number(event.target.value));
    setPage(1);
  };

  // "Go to page" box: with thousands of records the numbered buttons only
  // reach the first and last few pages, so reviewers need a direct jump.
  const [pageInput, setPageInput] = useState('');

  const submitPageJump = (event) => {
    if (event) event.preventDefault();
    const requested = parseInt(pageInput, 10);
    if (!Number.isFinite(requested)) return;
    // Clamp instead of rejecting: typing 999 on a 125-page list goes to 125.
    const target = Math.min(Math.max(requested, 1), totalPages);
    setPageInput('');
    if (target !== page) setPage(target);
  };

  // Fetch transactions whenever the requested table view changes. When a filter
  // changed while the reviewer was deeper in the list, reset to page 1 and skip
  // this run: the page change re-triggers the effect, so only the page 1
  // request reaches the backend instead of a wasted page N request first.
  useEffect(() => {
    if (prevFilterSignature.current !== filterSignature) {
      prevFilterSignature.current = filterSignature;
      if (page !== 1) {
        setPage(1);
        return;
      }
    }
    fetchTransactions().catch((error) => {
      console.error('Error fetching transactions:', error);
    });
  }, [fetchTransactions, filterSignature, page]);

  // Jump back to the top of the table whenever a new page is rendered so the
  // reviewer always starts at the first row of the page.
  useEffect(() => {
    const scrollContainer = transactionTableContainerRef.current;
    if (scrollContainer) scrollContainer.scrollTop = 0;
  }, [page, transactionsPerPage]);

  // Summary metrics depend only on the selected date range.
  useEffect(() => {
    fetchSummaryMetrics().catch((error) => {
      console.error('Error fetching summary metrics:', error);
    });
  }, [fetchSummaryMetrics]);



  const handleRowClick = async (transactionId) => {
    // Find the selected transaction
    const transaction = transactions.find(t => t.id === transactionId);
    if (!transaction) {
      console.error('Transaction not found:', transactionId);
      return;
    }

    const nextRowId = transaction.row_id ?? transaction.id;
    if (rightPanelOpen && Number(activeDetailsRowIdRef.current) === Number(nextRowId)) {
      return;
    }

    const previousRowId = activeDetailsRowIdRef.current;
    if (previousRowId && Number(previousRowId) !== Number(nextRowId)) {
      queryClient.cancelQueries({ queryKey: drawerDetailKey(previousRowId) });
      queryClient.cancelQueries({ queryKey: drawerMediaKey(previousRowId) });
      queryClient.cancelQueries({ queryKey: drawerPayloadKey(previousRowId) });
    }

    // Open panel immediately with available data; clear stale image from previous row.
    activeDetailsRowIdRef.current = transaction.row_id || transaction.id;
    setSelectedTransaction(transaction);
    setRightPanelOpen(true);
    setActiveTab(0);
    setStreetMapMode('map');
    setCommentSaved(false);
    setAllProcessedImages(null);
    setHybridReviewImageUrl(null);
    setProcessedStreetViewUrl(null);
    setTransactionDesignDetails(null);
    setCommentWarning('');


  };

  // Close right panel
  const handleClosePanel = () => {
    if (selectedRowId) {
      queryClient.cancelQueries({ queryKey: drawerDetailKey(selectedRowId) });
      queryClient.cancelQueries({ queryKey: drawerMediaKey(selectedRowId) });
      queryClient.cancelQueries({ queryKey: drawerPayloadKey(selectedRowId) });
    }
    activeDetailsRowIdRef.current = null;
    setRightPanelOpen(false);
    setSelectedTransaction(null);
    setTransactionDesignDetails(null);
    setAllProcessedImages(null);
    setHybridReviewImageUrl(null);
    setProcessedStreetViewUrl(null);
    setTerminalAddressDetails(null);
    setActiveTab(0);
    setStreetMapMode('map');
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Fetch transaction details from API
  const fetchTransactionDetails = async (transaction) => {
    setDetailsLoading(true);
    try {
      // Make API call to get design details
      const endpointPath = '/api/intellidrop/pathgenerator/';
      const url = window.location.hostname === 'localhost'
        ? endpointPath
        : `${backendRoot}/intellidrop/pathgenerator/`;

      const payload = {
        terminalDetails: {
          ID: transaction.terminal_id,
          feature_type: 'terminal',
          type: 'terminal',
          name: `Fiber Terminal: ${transaction.terminal_id}`,
          specification: '',
          placement_type: 'Aerial',
          geometry: {
            type: 'Point',
            coordinates: [
              transaction.terminal?.longitude,
              transaction.terminal?.latitude,
            ],
          },
        },
        bswDetails: {
          wire_center_clli: '',
          address_id: transaction.address_id,
          serving_address: transaction.full_address,
          serving_terminal: transaction.terminal_id,
        },
        addresses: [
          {
            address: {
              address_id: transaction.address_id,
              address_string: transaction.full_address,
              street_no: '',
              street_name: '',
              street_suffix: '',
              city: '',
              state: '',
              zipcode: '',
              landlord_consent: '',
              latitude: transaction.building?.latitude,
              longitude: transaction.building?.longitude,
              drop_length: '',
              drop_type: 'Aerial',
            },
          },
        ],
      };

      console.log('API Payload for Design Details:', payload);

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      console.log('API Response Status:', res.status, res.statusText);

      if (res.ok) {
        const data = await res.json();
        console.log('API Response Data:', data);

        // Extract the correct result for this address
        let resultData = null;
        if (data && Array.isArray(data.results) && data.results.length > 0) {
          const matched = data.results.find(r => r.address_id === transaction.address_id);
          if (matched && matched.data) {
            resultData = matched.data;
            console.log('Found matched result data:', resultData);
          } else if (data.results[0].data) {
            resultData = data.results[0].data;
            console.log('Using first result data:', resultData);
          }
        } else if (data && data.data) {
          resultData = data.data;
          console.log('Using direct data:', resultData);
        } else if (data && (data.cost || data.flowerpots || data.map_url || data["Converted Image"])) {
          // Check if the response has the design data directly at the root level
          resultData = data;
          console.log('Using full response as data (has design fields):', resultData);
        } else {
          resultData = data;
          console.log('Using full response as data:', resultData);
        }

        if (resultData) {
          // Set design details from API response
          const designDetails = {
            row_id: transaction.id,
            address_id: transaction.address_id,
            status: resultData.status || transaction.status.toLowerCase(),
            cost: resultData.cost || 0,
            flowerpots: Array.isArray(resultData.flowerpots) ? resultData.flowerpots.length : (resultData.flowerpots || 0),
            fp_id: Array.isArray(resultData.flowerpots) && resultData.flowerpots.length > 0 ? resultData.flowerpots[0].fp_id : (resultData.fp_id || "N/A"),
            map_url: resultData.map_url || "",
            building_id: resultData.building_id || 0,
            converted_image: resultData["Converted Image"] || resultData.converted_image || "",
            imagebase64: resultData.imagebase64 || "",
            version_id: resultData.version_id || 1,
            is_current: resultData.is_current !== undefined ? resultData.is_current : true,
            summary_date: transaction.received_date_time.split(' ')[0],
            tdp_temp_drop: resultData.tdp_temp_drop ?? null,
            tdp_aerial: resultData.tdp_aerial ?? null,
            tdp_hybrid: resultData.tdp_hybrid ?? null,
            tdp_scenario: resultData.tdp_scenario ?? null,
            tdp_due_date_interval: resultData.tdp_due_date_interval ?? null,
            tdp_complexity: resultData.tdp_complexity ?? null,
            tdp_raw: resultData.tdp_raw ?? null,
          };
          console.log('Setting design details:', designDetails);
          setTransactionDesignDetails(designDetails);
        } else {
          console.log('No result data found, setting null values');
          // Set all fields to null instead of hardcoded values
          setTransactionDesignDetails({
            row_id: null,
            address_id: transaction.address_id,
            status: null,
            cost: null,
            flowerpots: null,
            fp_id: null,
            map_url: null,
            building_id: null,
            converted_image: null,
            imagebase64: null,
            version_id: null,
            is_current: null,
            summary_date: null,
            tdp_temp_drop: null,
            tdp_aerial: null,
            tdp_hybrid: null,
            tdp_scenario: null,
            tdp_due_date_interval: null,
            tdp_complexity: null,
            tdp_raw: null,
          });
        }
      } else {
        console.log('API call failed, setting null values');
        // Set all fields to null instead of hardcoded values
        setTransactionDesignDetails({
          row_id: null,
          address_id: transaction.address_id,
          status: null,
          cost: null,
          flowerpots: null,
          fp_id: null,
          map_url: null,
          building_id: null,
          converted_image: null,
          imagebase64: null,
          version_id: null,
          is_current: null,
          summary_date: null,
          tdp_temp_drop: null,
          tdp_aerial: null,
          tdp_hybrid: null,
          tdp_scenario: null,
          tdp_due_date_interval: null,
          tdp_complexity: null,
          tdp_raw: null,
        });
      }

      // Mock terminal address details (keeping as is for now)
      setTerminalAddressDetails({
        row_num: transaction.id,
        ticket_id: transaction.ticket_id,
        terminal_id: transaction.terminal_id,
        addr1: transaction.full_address,
        addr1_lat: 39.781721000000,
        addr1_long: -89.650148000000,
        addr2: "456 Oak Avenue, Uptown, City, State 12345",
        addr2_lat: 40.7589,
        addr2_long: -73.9851,
        addr3: "789 Pine Street, Midtown, City, State 12345",
        addr3_lat: 40.7505,
        addr3_long: -73.9934
      });
    } catch (error) {
      console.error("Error fetching transaction details:", error);
      // Set all fields to null instead of hardcoded values
      setTransactionDesignDetails({
        row_id: null,
        address_id: transaction.address_id,
        status: null,
        cost: null,
        flowerpots: null,
        fp_id: null,
        map_url: null,
        building_id: null,
        converted_image: null,
        imagebase64: null,
        version_id: null,
        is_current: null,
        summary_date: null,
        tdp_temp_drop: null,
        tdp_aerial: null,
        tdp_hybrid: null,
        tdp_scenario: null,
        tdp_due_date_interval: null,
        tdp_complexity: null,
        tdp_raw: null,
      });
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleDownloadClick = () => {
    alert("Download functionality will be implemented when backend is ready!");
  };

  const downloadImage = async (url, filename) => {
    if (!url) return;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Image download failed with status ${response.status}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Image download failed:", error);
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleRetry = () => {
    setError(null);
    fetchTransactions();
    fetchSummaryMetrics();
  };

  const handleSubmitAction = async (transactionId) => {
    try {
      // spinner on this row
      setSubmittingTransactions(prev => new Set(prev).add(transactionId));

      const transaction = transactions.find(t => t.id === transactionId);
      if (!transaction) {
        alert('Transaction not found');
        return;
      }

      const designInputRowId =
        transaction.design_input_id ?? transaction.row_id ?? transaction.id;

      const res = await fetch(
        `${backendRoot}/api/intellidrop/process-request-save/${encodeURIComponent(designInputRowId)}/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ processing_mode: transactionProcessingMode }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || `API ${res.status} ${res.statusText}`);
      }

      setTransactionToProcess(null);
      await fetchTransactions();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: drawerDetailKey(designInputRowId) }),
        queryClient.invalidateQueries({ queryKey: drawerMediaKey(designInputRowId) }),
      ]);
    } catch (error) {
      console.error('Error calling process-request-save:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setSubmittingTransactions(prev => {
        const s = new Set(prev);
        s.delete(transactionId);
        return s;
      });
    }
  };

  // Transactions are filtered and paginated server-side.
  const allCurrentTransactions = Array.isArray(transactions) ? transactions : [];
  const totalPages = paginationInfo.total_pages > 0 ? paginationInfo.total_pages : 1;
  // Clamp to the server's view so the label and the highlighted page always
  // describe the rows actually rendered.
  const currentPage = Math.min(Math.max(page, 1), totalPages);
  const rangeStart = paginationInfo.count === 0 ? 0 : (currentPage - 1) * transactionsPerPage + 1;
  const rangeEnd = Math.min(currentPage * transactionsPerPage, paginationInfo.count);
  const currentTransactions = allCurrentTransactions;

  const rowsToShow = currentTransactions;

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setShowSuggestions(true);
  };

  const addressSuggestions = React.useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.trim().toLowerCase();
    return transactions
      .filter(t => t.full_address && t.full_address.toLowerCase().startsWith(q))
      .slice(0, 10);
  }, [searchQuery, transactions]);

  const handleSuggestionClick = (item) => {
    setSearchQuery('');
    setShowSuggestions(false);
    handleRowClick(item.id);
  };

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const saveNotesToBackend = async (transaction, noteValue) => {
    try {
      const designInputRowId =
        transaction?.design_input_id ?? transaction?.row_id ?? transaction?.id;
      if (!designInputRowId) return;

      const res = await fetch(
        `${backendRoot}/api/design-input/${encodeURIComponent(designInputRowId)}/notes/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ notes: noteValue }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || `Failed to save notes (${res.status})`);
      }
    } catch (e) {
      console.error('Failed to save notes:', e);
    }
  };

  const saveAuditToBackend = async (designInputId, auditData, txId) => {
    if (!designInputId) return;
    const now = new Date().toISOString();
    try {
      const response = await fetch(
        `${backendRoot}/api/transaction-audit/${encodeURIComponent(designInputId)}/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...auditData,
            audited_by: auditorName,
            audited_at: now,
          }),
        }
      );
      if (response.ok) {
        queryClient.invalidateQueries({ queryKey: drawerDetailKey(designInputId) });
      }
      if (txId) {
        setAuditMeta(prev => ({
          ...prev,
          [txId]: { audited_by: auditorName, audited_at: now },
        }));
      }
    } catch (e) {
      console.error('Failed to save audit:', e);
    }
  };

  const loadAuditFromBackend = async (transaction) => {
    const designInputId = transaction?.design_input_id ?? transaction?.row_id ?? transaction?.id;
    if (!designInputId) return;
    try {
      const res = await fetch(
        `${backendRoot}/api/transaction-audit/${encodeURIComponent(designInputId)}/`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (!res.ok) return;
      const data = await res.json();
      const txId = transaction.id;
      if (
        data.temp_drop_audit || data.tdp_indicator_audit ||
        data.bsw_design_audit || data.drop_type_audit ||
        data.temp_drop_comment || data.bsw_design_comment
      ) {
        setAuditSelections(prev => ({
          ...prev,
          [txId]: {
            tempDrop: data.temp_drop_audit || null,
            tdpIndicator: data.tdp_indicator_audit || null,
            bswDesign: data.bsw_design_audit || null,
            dropType: data.drop_type_audit || null,
          },
        }));
        if (data.temp_drop_comment || data.bsw_design_comment) {
          setSubmittedComments(prev => ({
            ...prev,
            [txId]: {
              tempDrop: data.temp_drop_comment || null,
              bswDesign: data.bsw_design_comment || null,
            },
          }));
        }
      }
      if (data.audited_by && data.audited_at) {
        setAuditMeta(prev => ({
          ...prev,
          [txId]: { audited_by: data.audited_by, audited_at: data.audited_at },
        }));
      }
    } catch (e) {
      console.error('Failed to load audit:', e);
    }
  };

  const formatValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return String(value);
};

  const handleDownloadExcel = async () => {
    try {
      setIsDownloading(true);

      // Fetch all records for this project in parallel
      const baseParams = new URLSearchParams();
      if (projectId) baseParams.append('project_id', projectId);
      if (startDate) baseParams.append('start_date', startDate);
      if (endDate) baseParams.append('end_date', endDate);
      baseParams.append('page_size', '1000');

      // Fetch page 1 first to get total_pages
      const firstParams = new URLSearchParams(baseParams);
      firstParams.set('page', '1');
      const firstResponse = await fetch(`${backendRoot}/api/design-input/?${firstParams.toString()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!firstResponse.ok) throw new Error(`HTTP error! status: ${firstResponse.status}`);
      const firstData = await firstResponse.json();
      const totalPages = firstData.total_pages || 1;
      let allRecords = firstData.results || [];

      // Fetch all remaining pages in parallel
      if (totalPages > 1) {
        const pageNumbers = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        const pageResults = await Promise.all(
          pageNumbers.map(async (pageNum) => {
            const params = new URLSearchParams(baseParams);
            params.set('page', pageNum);
            const response = await fetch(`${backendRoot}/api/design-input/?${params.toString()}`, {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' },
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            return data.results || [];
          })
        );
        allRecords = allRecords.concat(pageResults.flat());
      }

      const records = allRecords;

      // Build rows matching the table columns exactly — same field mapping as the transform in fetchTransactions
      const rows = records.map((item, index) => {
        const placementType = item.placement_type ?? item.terminal_placement_type ?? '';
        const dropType = item.tdp_hybrid === true ? 'Hybrid'
          : item.tdp_aerial === true ? 'Aerial'
          : item.tdp_aerial === false ? 'Buried'
          : item.drop_type ?? item.address_drop_type ?? item.irm_drop_type ?? '';
        const tempDropVal = item.processing_mode === 'bsw_only' ? 'Not requested' : (placementType?.toLowerCase() === 'aerial' && (item.tdp_aerial === true || dropType?.toLowerCase() === 'aerial'))
          ? 'Y'
          : formatIndicatorValue(item.tdp_temp_drop);

        const hybridVal = item.processing_mode === 'bsw_only' ? 'Not requested' : formatIndicatorValue(item.tdp_hybrid);

        const street = item.serving_address || item.full_address || item.address || item.address_string || item.service_address || '';
        const cityUp = (item.city || '').trim().toUpperCase();
        const fullAddress = cityUp && !street.toUpperCase().includes(cityUp)
          ? [street, [item.city, item.state].filter(Boolean).join(', '), item.zipcode].filter(Boolean).join(', ').replace(/, (\d{5}(?:-\d{4})?)$/, ' $1')
          : street;

        const receivedTs = item.created;
        const receivedDate = receivedTs && !isNaN(new Date(receivedTs).getTime()) ? new Date(receivedTs) : null;
        const receivedDateStr = receivedDate ? receivedDate.toISOString().split('T')[0] : '';
        const receivedTimeStr = receivedDate ? receivedDate.toISOString().split('T')[1].split('.')[0] : '';
        const timestamp = [receivedDateStr, receivedTimeStr].filter(Boolean).join(' | ');

        let processingStatus = item.processing_status;
        if (!processingStatus) processingStatus = 'Pending';
        else processingStatus = String(processingStatus).trim();
        // Keep the exported Status column consistent with the on-screen chip.
        if (item.needs_attention === true && /complete/i.test(processingStatus)) {
          processingStatus = 'Completed but needs attention';
        }

        const durationSec = item.processing_duration_seconds;
        let processingTime = '';
        if (durationSec != null) {
          const mins = Math.floor(durationSec / 60);
          const secs = Math.round(durationSec % 60);
          processingTime = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
        }

        return {
          'ID': item.row_id || index + 1,
          'Full Address': fullAddress,
          'Time Stamp': timestamp,
          'Status': processingStatus,
          'Temp Drop': tempDropVal,
          'Hybrid Indicator': hybridVal,
          'Processing Time': processingTime,
          'Placement Type': placementType,
          'Drop Type': dropType,
          'BSW Design': bswDesignStatus(item, placementType, dropType),
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');

      const projectLabel = projectId ? `project_${projectId}` : 'all_projects';
      let filename = `transactions_${projectLabel}`;
      if (startDate && endDate) filename = `transactions_${projectLabel}_${startDate}_to_${endDate}`;
      else if (startDate) filename = `transactions_${projectLabel}_from_${startDate}`;
      else if (endDate) filename = `transactions_${projectLabel}_until_${endDate}`;

      XLSX.writeFile(workbook, `${filename}.xlsx`);

    } catch (error) {
      console.error('Error downloading Excel file:', error);
      alert(`Failed to download file: ${error.message}`);
    } finally {
      setIsDownloading(false);
    }
  };


  const currentTransactionIndex = selectedTransaction
    ? transactions.findIndex(t => t.id === selectedTransaction.id)
    : -1;

  const checkCommentGate = () => {
    const txId = selectedTransaction?.id;
    const audit = auditSelections[txId] || {};
    const submitted = submittedComments[txId] || {};
    if (audit.tempDrop === 'x' && !submitted.tempDrop) {
      setCommentWarning('Please submit the Temp Drop comment before proceeding.');
      return false;
    }
    if (audit.bswDesign === 'x' && !submitted.bswDesign) {
      setCommentWarning('Please submit the BSW Design comment before proceeding.');
      return false;
    }
    return true;
  };

  const handlePrevTransaction = async () => {
    if (!checkCommentGate()) return;
    if (currentTransactionIndex > 0) {
      await handleRowClick(transactions[currentTransactionIndex - 1].id);
    }
  };

  const handleNextTransaction = async () => {
    if (!checkCommentGate()) return;
    if (currentTransactionIndex >= 0 && currentTransactionIndex < transactions.length - 1) {
      await handleRowClick(transactions[currentTransactionIndex + 1].id);
    }
  };

  return (
    <Box sx={{ background: pagePalette.background, minHeight: "100vh", color: pagePalette.ink }}>
      <Navbar />

      <Box sx={{ px: { xs: 2, md: 4 }, pt: 2, pb: 0 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, md: 2 },
            borderRadius: 2,
            border: `1px solid ${pagePalette.border}`,
            boxShadow: "0 14px 36px rgba(15, 23, 42, 0.07)",
            background: "linear-gradient(135deg, #FFFFFF 0%, #F6FBFF 58%, #F0FAF8 100%)",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center" }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: pagePalette.ink, lineHeight: 1.2, fontSize: { xs: "1.1rem", md: "1.3rem" }, letterSpacing: "-0.02em" }}>
                Transactions
              </Typography>
              <Typography sx={{ color: pagePalette.muted, mt: 0.3, fontSize: "0.78rem" }}>
                Dashboard
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", flex: 1, justifyContent: "center" }}>
              {[
                { icon: <DatasetIcon sx={{ fontSize: 14 }} />, label: "Input Records", value: summaryMetrics.totalInputCount || 0, color: pagePalette.primary },
{ icon: <TaskAltIcon sx={{ fontSize: 14 }} />, label: "Success Rate", value: summaryMetrics.successRate ? `${summaryMetrics.successRate}%` : 'N/A', color: pagePalette.success },
              ].map(({ icon, label, value, color }) => (
                <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1.5, py: 0.6, borderRadius: 10, border: `1px solid ${pagePalette.border}`, bgcolor: `${color}0D` }}>
                  <Box sx={{ color, display: "flex", alignItems: "center" }}>{icon}</Box>
                  <Typography sx={{ fontSize: "0.7rem", color: pagePalette.muted, fontWeight: 700 }}>{label}:</Typography>
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 800, color: pagePalette.ink }}>{value}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
              <Button
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => navigate(
                  projectId
                    ? `/append-data?project_id=${encodeURIComponent(projectId)}`
                    : '/append-data'
                )}
                sx={secondaryButtonSx}
              >
                Append Data
              </Button>
              <Button
                variant="contained"
                startIcon={isDownloading ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
                onClick={handleDownloadExcel}
                disabled={loading || isDownloading}
                sx={{ ...primaryButtonSx, bgcolor: pagePalette.accent, "&:hover": { bgcolor: "#2C8C8E" } }}
              >
                {isDownloading ? 'Downloading...' : 'Export'}
              </Button>
            </Box>
          </Box>
          <Box sx={{ mt: 1.5, pt: 1.25, borderTop: `1px solid ${pagePalette.border}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Box
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, cursor: "pointer", userSelect: "none", "&:hover": { opacity: 0.72 } }}
                onClick={() => setFilterOpen(!filterOpen)}
              >
                <FilterListIcon sx={{ color: pagePalette.primary, fontSize: 16 }} />
                <Typography sx={{ color: pagePalette.ink, fontWeight: 700, fontSize: "0.82rem" }}>Operational Filters</Typography>
                <ExpandMoreIcon sx={{ color: pagePalette.muted, fontSize: 18, transition: "transform 0.2s", transform: filterOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
              </Box>
            </Box>
            {filterOpen && (
              <Box sx={{ mt: 1.5 }}>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                  <TextField
                    type="date"
                    label="Start Date"
                    value={startDate || ''}
                    onChange={(e) => setStartDate(e.target.value)}
                    size="small"
                    sx={{ minWidth: 160 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    value={endDate || ''}
                    onChange={(e) => setEndDate(e.target.value)}
                    size="small"
                    sx={{ minWidth: 160 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    select
                    label="Status"
                    value={statusFilter || ''}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                    size="small"
                    sx={{ minWidth: 230 }}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value=""><em>All statuses</em></MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="completed_needs_attention">Completed but needs attention</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                    <MenuItem value="inserted">Inserted</MenuItem>
                    <MenuItem value="stopped">Stopped</MenuItem>
                  </TextField>
                  <Box ref={searchContainerRef} sx={{ position: 'relative', width: 340 }}>
                    <TextField
                      placeholder="Search order, ticket, address"
                      variant="outlined"
                      size="small"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      onFocus={() => searchQuery.trim() && setShowSuggestions(true)}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          bgcolor: "#fff",
                          '&:hover fieldset': { borderColor: pagePalette.primary },
                          '&.Mui-focused fieldset': { borderColor: pagePalette.primary },
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: pagePalette.primary }} />
                          </InputAdornment>
                        ),
                        endAdornment: searchQuery.trim() && (searchPending || loading) ? (
                          <InputAdornment position="end">
                            <CircularProgress
                              size={16}
                              aria-label={searchPending ? "Waiting to search" : "Searching transactions"}
                              sx={{ color: pagePalette.primary }}
                            />
                          </InputAdornment>
                        ) : null,
                      }}
                      inputProps={{ "aria-label": "Search transactions" }}
                    />
                    {showSuggestions && addressSuggestions.length > 0 && (
                      <Paper elevation={4} sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1300, mt: 0.5, borderRadius: 1.5, overflow: 'hidden', border: `1px solid ${pagePalette.border}` }}>
                        {addressSuggestions.map((item) => (
                          <Box
                            key={item.id}
                            onMouseDown={() => handleSuggestionClick(item)}
                            sx={{
                              px: 2, py: 1, cursor: 'pointer', fontSize: '0.82rem', color: pagePalette.ink,
                              borderBottom: `1px solid ${pagePalette.border}`,
                              '&:last-child': { borderBottom: 'none' },
                              '&:hover': { bgcolor: '#EEF6FF' },
                            }}
                          >
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{item.full_address}</Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: pagePalette.muted }}>ID: {item.id}</Typography>
                          </Box>
                        ))}
                      </Paper>
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setStartDate('');
                      setEndDate('');
                      setTempDropFilter('');
                      setHybridFilter('');
                      setAerialFilter('');
                      setScenarioFilter('');
                      setStatusFilter('');
                      setSearchQuery('');
                      setPage(1);
                    }}
                    sx={secondaryButtonSx}
                  >
                    Clear Filter
                  </Button>
                </Box>
                {/* Temp Drop, Hybrid, Aerial, Status, Scenario filters hidden */}
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      {error && (
        <Box sx={{ px: 4, mb: 2 }}>
          <Alert severity="error" role="alert" action={<Button color="inherit" size="small" onClick={handleRetry}>RETRY</Button>}>
            {typeof error === "object" ? (error.detail || JSON.stringify(error)) : error}
          </Alert>
        </Box>
      )}

      <Box sx={{ px: { xs: 2, md: 4 }, pt: 1, pb: 2 }}>
        <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden", border: `1px solid ${pagePalette.border}`, boxShadow: "0 14px 32px rgba(15, 23, 42, 0.08)", width: '100%' }}>
          <Box sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, borderBottom: `1px solid ${pagePalette.border}`, bgcolor: pagePalette.surface }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate('/projects')}
              sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'none', borderColor: pagePalette.border, color: pagePalette.muted, px: 1.5, py: 0.5, borderRadius: 1, minWidth: 80, '&:hover': { borderColor: pagePalette.primary, color: pagePalette.primary, bgcolor: pagePalette.primaryLight } }}
            >
              ← Projects
            </Button>
            <Typography sx={{ fontWeight: 800, color: pagePalette.ink, flex: 1, textAlign: 'center' }}>Transaction Queue</Typography>
            <Chip
              label={`${transactions.length.toLocaleString()} shown of ${(paginationInfo.count || 0).toLocaleString()} records`}
              size="small"
              sx={{ bgcolor: pagePalette.primaryLight, color: pagePalette.primary, fontWeight: 800, border: "1px solid #CFE2F3" }}
            />
          </Box>
          <TableContainer ref={transactionTableContainerRef} sx={{ height: "calc(100vh - 300px)", minHeight: 400, overflow: "auto", bgcolor: pagePalette.surface }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortOrder} sx={{ bgcolor: "#EEF2F7", color: pagePalette.ink, fontWeight: 800, py: 1.25 }}>
                    <TableSortLabel
                      active
                      direction={sortOrder}
                      onClick={() => {
                        setPage(1);
                        setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                      }}
                      sx={{ color: 'inherit', '&.Mui-active': { color: 'inherit' }, '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                    >
                      ID
                    </TableSortLabel>
                  </TableCell>
                  {/* "Ticket No.", "BSW Due Date", "Reason" hidden — may be re-added in future */}
                  {["Full Address", "Time Stamp", "Status", "Temp Drop", "Hybrid Indicator", "Processing Time", "Placement Type", "Drop Type", "BSW Design"].map((heading) => (
                    <TableCell key={heading} sx={{ bgcolor: "#EEF2F7", color: pagePalette.ink, fontWeight: 800, py: 1.25 }}>
                      {heading}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: Math.min(transactionsPerPage, 8) }).map((_, rowIndex) => (
                    <TableRow key={`transaction-skeleton-${rowIndex}`}>
                      {Array.from({ length: 12 }).map((__, cellIndex) => (
                        <TableCell key={`transaction-skeleton-${rowIndex}-${cellIndex}`} sx={{ py: 1.15 }}>
                          <Skeleton animation="wave" width={cellIndex === 1 ? 180 : cellIndex === 0 ? 52 : 88} height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : rowsToShow.length > 0 ? (
                  rowsToShow.map((item, index) => (
                    <TableRow
                      key={item.id}
                      hover={!item.isEmpty}
                      sx={{
                        cursor: item.isEmpty ? "default" : "pointer",
                        background: item.isEmpty
                          ? "#fff"
                          : selectedTransaction?.id === item.id
                            ? "linear-gradient(90deg, #B8DCEF 0%, #CCE9F8 40%, #DFF1FF 75%, #EBF6FF 100%)"
                            : index % 2 === 0 ? "#FFFFFF" : "#F8FBFD",
                        "&:hover": {
                          background: item.isEmpty
                            ? "transparent"
                            : selectedTransaction?.id === item.id
                              ? "linear-gradient(90deg, #A8D2EC 0%, #BEDFFA 40%, #D3ECFF 75%, #E0F3FF 100%)"
                              : "#EEF6FF",
                        },
                        height: "34px",
                        "& .MuiTableCell-root": {
                          borderBottom: `1px solid ${pagePalette.border}`,
                          color: pagePalette.ink,
                          fontSize: "0.82rem",
                        },
                        ...(selectedTransaction?.id === item.id && !item.isEmpty && {
                          "& .MuiTableCell-root:first-child": {
                            borderLeft: `3px solid ${pagePalette.primary}`,
                          },
                        }),
                      }}
                      tabIndex={item.isEmpty ? -1 : 0}
                      aria-label={item.isEmpty ? undefined : `Open transaction ${item.id}`}
                      onMouseEnter={item.isEmpty ? undefined : () => scheduleDrawerPrefetch(item)}
                      onMouseLeave={cancelDrawerPrefetch}
                      onFocus={item.isEmpty ? undefined : () => scheduleDrawerPrefetch(item)}
                      onBlur={cancelDrawerPrefetch}
                      onKeyDown={item.isEmpty ? undefined : (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleRowClick(item.id);
                        }
                      }}
                      onClick={item.isEmpty ? undefined : () => handleRowClick(item.id)}
                    >
                      <TableCell sx={{ fontWeight: 800 }}>{item.isEmpty ? '\u00A0' : item.id}</TableCell>
                      {/* <TableCell sx={{ fontWeight: 700 }}>{item.isEmpty ? '\u00A0' : (item.ticket_id || '').toUpperCase()}</TableCell> */}
                      {/* <TableCell>{item.isEmpty ? '\u00A0' : item.bsw_due_date}</TableCell> */}
                      <TableCell sx={{ minWidth: 220 }}>{item.isEmpty ? '\u00A0' : item.full_address}</TableCell>
                      <TableCell sx={{ minWidth: 150, whiteSpace: 'nowrap' }}>{item.isEmpty ? '\u00A0' : [item.received_date_time, item.received_time].filter(Boolean).join(' | ')}</TableCell>
                      <TableCell sx={{ minWidth: 130 }}>
                        {item.isEmpty ? '\u00A0' : (() => {
                          const status = item.processing_status || 'Pending';
                          let normalizedStatus = String(status).trim();
                          // needs_attention is set when the design's building polygon
                          // crosses its parcel boundary; processing_status stays
                          // 'completed', so the distinction is display-only.
                          if (item.needs_attention && /complete/i.test(normalizedStatus)) {
                            normalizedStatus = 'Completed but needs attention';
                          }

                          return (
                            <Chip
                              label={normalizedStatus}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.72rem',
                                fontWeight: 800,
                                textTransform: 'capitalize',
                                border: "1px solid",
                                ...getStatusChipSx(normalizedStatus),
                              }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ height: "18px" }}>
                        {item.isEmpty ? ' ' : (
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                            <span>{item.processing_mode === 'bsw_only' ? 'Not requested' : (item.placement_type?.toLowerCase() === 'aerial' && (item.tdp_aerial === true || item.drop_type?.toLowerCase() === 'aerial')) ? 'Y' : (item.tdp_indicator ?? '')}</span>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAuditSelections(prev => { const cur = prev[item.id] || {}; return { ...prev, [item.id]: { ...cur, tdpIndicator: cur.tdpIndicator === 'tick' ? null : 'tick' } }; }); }} sx={{ p: 0.1 }}>
                                <CheckCircleIcon sx={{ fontSize: 12, color: auditSelections[item.id]?.tdpIndicator === 'tick' ? pagePalette.success : '#C4C4C4', transition: 'color 0.15s' }} />
                              </IconButton>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAuditSelections(prev => { const cur = prev[item.id] || {}; return { ...prev, [item.id]: { ...cur, tdpIndicator: cur.tdpIndicator === 'x' ? null : 'x' } }; }); }} sx={{ p: 0.1 }}>
                                <CancelIcon sx={{ fontSize: 12, color: auditSelections[item.id]?.tdpIndicator === 'x' ? pagePalette.danger : '#C4C4C4', transition: 'color 0.15s' }} />
                              </IconButton>
                            </Box>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ height: "18px" }}>{item.isEmpty ? ' ' : item.processing_mode === 'bsw_only' ? 'Not requested' : (item.hybrid_indicator ?? '')}</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>{item.isEmpty ? '\u00A0' : formatProcessingDuration(item.processing_duration_seconds)}</TableCell>
                      {/* Reason column hidden \u2014 may be re-added in future
                      <TableCell sx={{ minWidth: 170 }}>
                        {item.isEmpty ? '\u00A0' : (
                          <Select
                            value={noteOptions.includes(notesMap[item.id]) ? notesMap[item.id] : ''}
                            onChange={(e) => {
                              e.stopPropagation();
                              const val = e.target.value;
                              setNotesMap(prev => ({ ...prev, [item.id]: val }));
                              const transaction = transactions.find(t => t.id === item.id);
                              if (transaction) {
                                saveNotesToBackend(transaction, val);
                              }
                            }}
                            size="small"
                            displayEmpty
                            sx={{ fontSize: '0.75rem', height: 30, bgcolor: "#fff", borderRadius: 1 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MenuItem value=""><em>None</em></MenuItem>
                            <MenuItem value="Incorrect payload">Incorrect payload</MenuItem>
                            <MenuItem value="Yes">Yes</MenuItem>
                            <MenuItem value="No">No</MenuItem>
                            <MenuItem value="Partial">Partial</MenuItem>
                          </Select>
                        )}
                      </TableCell>
                      */}
                      {(() => {
                        if (item.isEmpty) return (
                          <>
                            <TableCell>{'\u00A0'}</TableCell>
                            <TableCell>{'\u00A0'}</TableCell>
                            <TableCell>{'\u00A0'}</TableCell>
                          </>
                        );
                        const sv = getTransactionSummaryValues(item, null, null);
                        const dcResult = item.drop_classifier_result
                          ? item.drop_classifier_result.charAt(0).toUpperCase() + item.drop_classifier_result.slice(1).toLowerCase()
                          : null;
                        return (
                          <>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{sv.placementType || '\u2014'}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{sv.dropType || '\u2014'}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{bswDesignStatus(item, sv.placementType, sv.dropType)}</TableCell>
                          </>
                        );
                      })()}
                    </TableRow>
                  ))
                ) : (
                  <TableRow><TableCell colSpan={12} align="center"><Box sx={{ py: 5 }}><Typography sx={{ color: pagePalette.ink, fontWeight: 800 }}>{debouncedSearchQuery ? `No transactions found matching "${debouncedSearchQuery}".` : "No transactions found."}</Typography><Typography sx={{ color: pagePalette.muted, mt: 0.5 }}>Adjust filters or clear the date range to broaden the queue.</Typography></Box></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {paginationInfo.count > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderTop: `1px solid ${pagePalette.border}`, bgcolor: "#FBFDFF", flexWrap: "wrap", gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                  sx={{ color: pagePalette.muted, fontWeight: 700, fontSize: "0.82rem" }}
                >
                  {loading
                    ? `Loading page ${currentPage.toLocaleString()} of ${totalPages.toLocaleString()}...`
                    : `Showing ${rangeStart.toLocaleString()}-${rangeEnd.toLocaleString()} of ${paginationInfo.count.toLocaleString()} records (page ${currentPage.toLocaleString()} of ${totalPages.toLocaleString()})`}
                </Typography>
                <FormControl size="small">
                  <Select
                    value={transactionsPerPage}
                    onChange={handleRowsPerPageChange}
                    disabled={loading}
                    inputProps={{ 'aria-label': 'Rows per page' }}
                    sx={{ fontSize: '0.8rem', fontWeight: 700, borderRadius: 1.5, bgcolor: '#FFFFFF' }}
                  >
                    {[25, 50, 100, 250].map((size) => (
                      <MenuItem key={size} value={size} sx={{ fontSize: '0.8rem' }}>{`${size} / page`}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                {totalPages > 1 && (
                  <Box
                    component="form"
                    onSubmit={submitPageJump}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                  >
                    <Typography component="label" htmlFor="transaction-page-jump" sx={{ color: pagePalette.muted, fontWeight: 700, fontSize: '0.82rem' }}>
                      Go to page
                    </Typography>
                    <TextField
                      id="transaction-page-jump"
                      value={pageInput}
                      onChange={(event) => setPageInput(event.target.value.replace(/[^0-9]/g, ''))}
                      placeholder={String(currentPage)}
                      size="small"
                      disabled={loading}
                      inputProps={{
                        inputMode: 'numeric',
                        'aria-label': `Go to page number, between 1 and ${totalPages}`,
                        style: { textAlign: 'center', padding: '6px 4px', fontWeight: 700, fontSize: '0.8rem' },
                      }}
                      sx={{ width: 72, '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#FFFFFF' } }}
                    />
                    <Button
                      type="submit"
                      size="small"
                      variant="outlined"
                      disabled={loading || pageInput === ''}
                      sx={{
                        minWidth: 52,
                        borderColor: pagePalette.primary,
                        color: pagePalette.primary,
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: 700,
                        '&:hover': { borderColor: '#1B4965', color: '#1B4965', bgcolor: pagePalette.primaryLight },
                      }}
                    >
                      Go
                    </Button>
                  </Box>
                )}
                <Pagination
                aria-label="Transaction pagination"
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                disabled={loading}
                color="primary"
                shape="rounded"
                siblingCount={1}
                boundaryCount={1}
                showFirstButton
                showLastButton
                getItemAriaLabel={(type, pageNumber, selected) => {
                  if (type === 'page') return selected ? `Page ${pageNumber}, current page` : `Go to page ${pageNumber}`;
                  if (type === 'first') return 'Go to first page';
                  if (type === 'last') return `Go to last page, page ${totalPages}`;
                  if (type === 'previous') return 'Go to previous page';
                  if (type === 'next') return 'Go to next page';
                  return undefined;
                }}
                sx={{ '& .MuiPaginationItem-root': { fontWeight: 700, fontSize: '0.8rem' } }}
                />
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Right Panel with Transaction Details */}
      <Drawer
        anchor="right"
        open={rightPanelOpen}
        onClose={handleClosePanel}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 680, md: 780 },
            background: pagePalette.background,
            borderLeft: `1px solid ${pagePalette.border}`,
          },
        }}
      >
        {selectedTransaction && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{
              p: 2.5,
              bgcolor: "#EEF2F7",
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: `1px solid ${pagePalette.border}`,
            }}>
              <Box>
                <Typography sx={{ color: pagePalette.ink, fontSize: '1.2rem', fontWeight: 800, lineHeight: 1.3 }}>
                  {selectedTransaction?.full_address || ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  size="small"
                  disabled={currentTransactionIndex <= 0}
                  onClick={handlePrevTransaction}
                  sx={{ color: pagePalette.ink, bgcolor: 'rgba(0,0,0,0.06)', '&:hover': { bgcolor: 'rgba(0,0,0,0.12)' }, '&.Mui-disabled': { color: 'rgba(0,0,0,0.2)' } }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ color: pagePalette.muted, fontSize: '0.75rem', fontWeight: 600, px: 0.5, minWidth: 38, textAlign: 'center' }}>
                  {currentTransactionIndex >= 0 ? `${currentTransactionIndex + 1}/${transactions.length}` : ''}
                </Typography>
                <IconButton
                  size="small"
                  disabled={currentTransactionIndex < 0 || currentTransactionIndex >= transactions.length - 1}
                  onClick={handleNextTransaction}
                  sx={{ color: pagePalette.ink, bgcolor: 'rgba(0,0,0,0.06)', '&:hover': { bgcolor: 'rgba(0,0,0,0.12)' }, '&.Mui-disabled': { color: 'rgba(0,0,0,0.2)' } }}
                >
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  title="Process transaction"
                  aria-label="Process transaction"
                  size="small"
                  disabled={submittingTransactions.has(selectedTransaction.id)}
                  onClick={() => {
                    setTransactionProcessingMode(selectedTransaction.processing_mode || 'tdp_bsw');
                    setTransactionToProcess(selectedTransaction.id);
                  }}
                  sx={{ color: pagePalette.ink, bgcolor: 'rgba(0,0,0,0.06)', '&:hover': { bgcolor: 'rgba(0,0,0,0.12)' } }}
                >
                  <PlayArrowIcon fontSize="small" />
                </IconButton>
                <IconButton onClick={handleClosePanel} size="small" sx={{ color: pagePalette.ink, bgcolor: 'rgba(0,0,0,0.06)', '&:hover': { bgcolor: 'rgba(0,0,0,0.12)' }, ml: 0.5 }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Transaction Summary */}
            <Box sx={{ p: 2, borderBottom: `1px solid ${pagePalette.border}`, bgcolor: '#FBFDFF' }}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${pagePalette.border}`, bgcolor: pagePalette.surface }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Box sx={{ flexShrink: 0 }}>
                    <Typography sx={{ color: pagePalette.muted, fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0 }}>
                      Selected Transaction
                    </Typography>
                    <Typography sx={{ fontWeight: 800, color: pagePalette.ink, fontSize: '0.88rem', mt: 0.1, lineHeight: 1.3 }}>
                      {selectedTransaction.ticket_id || selectedTransaction.id || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'nowrap', justifyContent: 'flex-end', flex: 1, minWidth: 0 }}>
                    {(() => {
                      const summaryValues = getTransactionSummaryValues(selectedTransaction, transactionDesignDetails, terminalAddressDetails);
                      const txId = selectedTransaction?.id;
                      const audit = auditSelections[txId] || {};

                      const toggleAudit = (field, which) => {
                        const cur = auditSelections[txId] || {};
                        const newVal = cur[field] === which ? null : which;
                        const updates = { [field]: newVal };
                        if (field === 'tempDrop') updates.tdpIndicator = newVal;
                        const next = { ...cur, ...updates };
                        setAuditSelections(prev => ({ ...prev, [txId]: next }));
                        const designInputId = selectedTransaction?.design_input_id ?? selectedTransaction?.row_id ?? selectedTransaction?.id;
                        const saved = submittedComments[txId] || {};
                        saveAuditToBackend(designInputId, {
                          temp_drop_audit: next.tempDrop || null,
                          tdp_indicator_audit: next.tdpIndicator || null,
                          bsw_design_audit: next.bswDesign || null,
                          drop_type_audit: next.dropType || null,
                          temp_drop_comment: saved.tempDrop || null,
                          bsw_design_comment: saved.bswDesign || null,
                        }, txId);
                        if (next[field] !== 'x') setCommentWarning('');
                      };

                      const AuditIcons = ({ field }) => (
                        <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0, ml: 'auto', pl: 0.5 }}>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleAudit(field, 'tick'); }} sx={{ p: 0.1 }}>
                            <CheckCircleIcon sx={{ fontSize: 13, color: audit[field] === 'tick' ? pagePalette.success : '#C4C4C4', transition: 'color 0.18s' }} />
                          </IconButton>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleAudit(field, 'x'); }} sx={{ p: 0.1 }}>
                            <CancelIcon sx={{ fontSize: 13, color: audit[field] === 'x' ? pagePalette.danger : '#C4C4C4', transition: 'color 0.18s' }} />
                          </IconButton>
                        </Box>
                      );

                      const boxSx = {
                        flex: '1 1 0',
                        minWidth: 0,
                        border: `1px solid ${pagePalette.border}`,
                        borderRadius: 1.5,
                        bgcolor: '#FBFDFF',
                        px: 1,
                        py: 0.5,
                        overflow: 'hidden',
                      };

                      const labelSx = { color: pagePalette.muted, fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
                      const valueSx = { color: pagePalette.ink, fontSize: '0.84rem', fontWeight: 800, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

                      return (
                        <>
                          <Box sx={boxSx}>
                            <Typography sx={labelSx}>Placement Type</Typography>
                            <Typography sx={valueSx}>{summaryValues.placementType}</Typography>
                          </Box>

                          <Box sx={{ ...boxSx, display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                              <Typography sx={labelSx}>Drop Type</Typography>
                              <Typography sx={valueSx}>{summaryValues.dropType}</Typography>
                            </Box>
                            {summaryValues.placementType === 'Aerial' && <AuditIcons field="dropType" />}
                          </Box>

                          {/* Drop Classifier hidden */}

                          <Box sx={{ ...boxSx, display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                              <Typography sx={labelSx}>Temp Drop</Typography>
                              <Typography sx={valueSx}>{selectedTransaction.processing_mode === 'bsw_only' ? 'Not requested' : (summaryValues.placementType?.toLowerCase() === 'aerial' && summaryValues.dropType?.toLowerCase() === 'aerial') ? 'Eligible' : (summaryValues.tempDrop ? tempDropLabel(summaryValues.tempDrop) : summaryValues.tempDrop)}</Typography>
                            </Box>
                            {summaryValues.dropType === 'Buried' && <AuditIcons field="tempDrop" />}
                          </Box>

                          <Box sx={{ ...boxSx, display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                              <Typography sx={labelSx}>BSW Design</Typography>
                              <Typography sx={valueSx}>{bswDesignStatus(selectedTransaction, summaryValues.placementType, summaryValues.dropType)}</Typography>
                            </Box>
                            <AuditIcons field="bswDesign" />
                          </Box>
                        </>
                      );
                    })()}
                  </Box>
                </Box>
                {(() => {
                  const txId = selectedTransaction?.id;
                  const audit = auditSelections[txId] || {};
                  const comments = commentSelections[txId] || {};
                  const submitted = submittedComments[txId] || {};
                  const setComment = (field, val) => setCommentSelections(prev => ({ ...prev, [txId]: { ...(prev[txId] || {}), [field]: val } }));
                  const saveComment = (field) => {
                    const val = field === 'tempDrop'
                      ? (comments.tempDrop === 'Others' ? comments.tempDropOther : comments.tempDrop)
                      : comments[field];
                    if (!val || !val.trim()) return;
                    const trimmed = val.trim();
                    setSubmittedComments(prev => ({ ...prev, [txId]: { ...(prev[txId] || {}), [field]: trimmed } }));
                    setCommentWarning('');
                    const designInputId = selectedTransaction?.design_input_id ?? selectedTransaction?.row_id ?? selectedTransaction?.id;
                    const currentAudit = auditSelections[txId] || {};
                    saveAuditToBackend(designInputId, {
                      temp_drop_audit: currentAudit.tempDrop || null,
                      tdp_indicator_audit: currentAudit.tdpIndicator || null,
                      bsw_design_audit: currentAudit.bswDesign || null,
                      drop_type_audit: currentAudit.dropType || null,
                      temp_drop_comment: field === 'tempDrop' ? trimmed : ((submittedComments[txId] || {}).tempDrop || null),
                      bsw_design_comment: field === 'bswDesign' ? trimmed : ((submittedComments[txId] || {}).bswDesign || null),
                    }, txId);
                  };
                  if (audit.tempDrop !== 'x' && audit.bswDesign !== 'x') return null;
                  const saveBtnSx = {
                    mt: 0.75, alignSelf: 'flex-end', fontSize: '0.72rem', fontWeight: 700,
                    textTransform: 'none', px: 2, py: 0.5, borderRadius: 1,
                    bgcolor: pagePalette.primary, color: '#fff',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                    '&:hover': { bgcolor: pagePalette.primaryDark || '#1a5276' },
                  };
                  return (
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 1.25, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                      {audit.tempDrop === 'x' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: pagePalette.muted, textTransform: 'uppercase', letterSpacing: 0 }}>Temp Drop Comment</Typography>
                          {submitted.tempDrop ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Box sx={{ bgcolor: '#f4f6f8', border: '1px solid #dde1e7', borderRadius: 1, px: 1.5, py: 0.75, minWidth: 175 }}>
                                <Typography sx={{ fontSize: '0.78rem', color: pagePalette.text }}>{submitted.tempDrop}</Typography>
                              </Box>
                              <Button size="small" variant="text" onClick={() => setSubmittedComments(prev => ({ ...prev, [txId]: { ...(prev[txId] || {}), tempDrop: null } }))} sx={{ alignSelf: 'flex-start', fontSize: '0.68rem', textTransform: 'none', color: pagePalette.muted, p: 0, minWidth: 0 }}>Edit</Button>
                            </Box>
                          ) : (
                            <>
                              <Select
                                value={comments.tempDrop || ''}
                                onChange={(e) => setComment('tempDrop', e.target.value)}
                                size="small"
                                displayEmpty
                                sx={{ fontSize: '0.78rem', height: 30, minWidth: 175, bgcolor: '#fff', borderRadius: 1 }}
                              >
                                <MenuItem value=""><em style={{ fontSize: '0.78rem' }}>Select a comment</em></MenuItem>
                                <MenuItem value="Road in Between" sx={{ fontSize: '0.78rem' }}>Road in Between</MenuItem>
                                <MenuItem value="Multiple Driveways" sx={{ fontSize: '0.78rem' }}>Multiple Driveways</MenuItem>
                                <MenuItem value="Others" sx={{ fontSize: '0.78rem' }}>Others</MenuItem>
                              </Select>
                              {comments.tempDrop === 'Others' && (
                                <TextField
                                  size="small"
                                  placeholder="Type your comment..."
                                  value={comments.tempDropOther || ''}
                                  onChange={(e) => setComment('tempDropOther', e.target.value)}
                                  sx={{ mt: 0.5, minWidth: 175, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.75 } }}
                                />
                              )}
                              {(comments.tempDrop && comments.tempDrop !== 'Others') || (comments.tempDrop === 'Others' && comments.tempDropOther?.trim()) ? (
                                <Button size="small" variant="contained" disableElevation onClick={() => saveComment('tempDrop')} sx={saveBtnSx}>Save Comment</Button>
                              ) : null}
                            </>
                          )}
                        </Box>
                      )}
                      {audit.bswDesign === 'x' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: pagePalette.muted, textTransform: 'uppercase', letterSpacing: 0 }}>BSW Design Comment</Typography>
                          {submitted.bswDesign ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Box sx={{ bgcolor: '#f4f6f8', border: '1px solid #dde1e7', borderRadius: 1, px: 1.5, py: 0.75, minWidth: 200 }}>
                                <Typography sx={{ fontSize: '0.78rem', color: pagePalette.text }}>{submitted.bswDesign}</Typography>
                              </Box>
                              <Button size="small" variant="text" onClick={() => setSubmittedComments(prev => ({ ...prev, [txId]: { ...(prev[txId] || {}), bswDesign: null } }))} sx={{ alignSelf: 'flex-start', fontSize: '0.68rem', textTransform: 'none', color: pagePalette.muted, p: 0, minWidth: 0 }}>Edit</Button>
                            </Box>
                          ) : (
                            <>
                              <TextField
                                size="small"
                                placeholder="Type your comment..."
                                value={comments.bswDesign || ''}
                                onChange={(e) => setComment('bswDesign', e.target.value)}
                                multiline
                                minRows={1}
                                maxRows={3}
                                sx={{ minWidth: 200, '& .MuiInputBase-input': { fontSize: '0.78rem' } }}
                              />
                              {comments.bswDesign?.trim() && (
                                <Button size="small" variant="contained" disableElevation onClick={() => saveComment('bswDesign')} sx={saveBtnSx}>Save Comment</Button>
                              )}
                            </>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })()}
                {commentWarning && (
                  <Alert severity="warning" onClose={() => setCommentWarning('')} sx={{ mt: 1, fontSize: '0.75rem', py: 0.25 }}>
                    {commentWarning}
                  </Alert>
                )}
                {auditMeta[selectedTransaction?.id] && (
                  <Typography sx={{ fontSize: '0.65rem', color: pagePalette.muted, mt: 0.75 }}>
                    Audited by <strong>{auditMeta[selectedTransaction.id].audited_by}</strong>
                    {' · '}
                    {new Date(auditMeta[selectedTransaction.id].audited_at).toLocaleString()}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ color: pagePalette.muted, mt: 1.25 }}>
                  {selectedTransaction.full_address || 'No address available'}
                </Typography>
              </Paper>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: `1px solid ${pagePalette.border}`, bgcolor: pagePalette.surface, px: 1 }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    color: pagePalette.muted,
                    fontWeight: 800,
                    textTransform: 'none',
                    minHeight: 52,
                    '&.Mui-selected': {
                      color: pagePalette.primary,
                    },
                  },
                  '& .MuiTabs-indicator': {
                    background: pagePalette.primary,
                    height: 3,
                    borderRadius: 3,
                  },
                }}
              >
                <Tab label="Design" />
                <Tab label="Map" />
                <Tab label="Details" />
                <Tab label="Payload Details" />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{
              flex: 1,
              overflow: 'auto',
              '& [role="tabpanel"] > .MuiBox-root': { p: 2.5 },
              '& .MuiCard-root': detailCardSx,
              '& .MuiCardContent-root > .MuiBox-root > .MuiBox-root[style], & .MuiCardContent-root > .MuiBox-root > .MuiBox-root': {
                borderColor: pagePalette.border,
              },
              '& .MuiCardContent-root .MuiTypography-body1:first-of-type': {
                color: `${pagePalette.ink} !important`,
              },
            }}>
              {drawerDetailQuery.isError && (
                <Alert
                  severity="error"
                  action={<Button color="inherit" size="small" onClick={() => drawerDetailQuery.refetch()}>Retry</Button>}
                  sx={{ m: 2, mb: 0 }}
                >
                  Some transaction details could not be loaded. Queue values remain available.
                </Alert>
              )}
              <>
                  {/* Design Tab - image and processed images */}
                  <TabPanel value={activeTab} index={0}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ color: pagePalette.ink, fontWeight: 800 }}>
                        {transactionDesignDetails?.is_historical ? 'Previous Design Image' : 'Design Image'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!selectedRowId}
                        onClick={() => setDesignEditOpen(true)}
                      >
                        Edit design
                      </Button>
                      <IconButton
                        aria-label="Copy image"
                        size="small"
                        onClick={async () => {
                          try {
                            const imageElement = designImageRef.current;

                            if (!imageElement) {
                              alert('Image not found. Please wait for the image to load.');
                              return;
                            }

                            // Wait for image to be fully loaded
                            if (!imageElement.complete || imageElement.naturalWidth === 0) {
                              await new Promise((resolve, reject) => {
                                const timeout = setTimeout(() => {
                                  reject(new Error('Image load timeout'));
                                }, 5000);

                                imageElement.onload = () => {
                                  clearTimeout(timeout);
                                  resolve();
                                };

                                imageElement.onerror = () => {
                                  clearTimeout(timeout);
                                  reject(new Error('Image failed to load'));
                                };

                                // If already loaded, resolve immediately
                                if (imageElement.complete && imageElement.naturalWidth > 0) {
                                  clearTimeout(timeout);
                                  resolve();
                                }
                              });
                            }

                            // Create canvas and draw image
                            const canvas = document.createElement('canvas');
                            canvas.width = imageElement.naturalWidth;
                            canvas.height = imageElement.naturalHeight;
                            const ctx = canvas.getContext('2d');

                            // Draw the image to canvas
                            ctx.drawImage(imageElement, 0, 0);

                            // Convert to blob and copy using multiple methods
                            canvas.toBlob(async (blob) => {
                              if (!blob) {
                                alert('Failed to process image. Please try right-click > Copy image.');
                                return;
                              }

                              let copySuccess = false;

                              // Method 1: Try modern Clipboard API with image/png
                              try {
                                if (navigator.clipboard && window.ClipboardItem) {
                                  // Try with 'image/png' first
                                  try {
                                    await navigator.clipboard.write([
                                      new ClipboardItem({
                                        'image/png': blob
                                      })
                                    ]);
                                    copySuccess = true;
                                  } catch (pngError) {
                                    // Try with the blob's actual type
                                    const actualType = blob.type || 'image/png';
                                    await navigator.clipboard.write([
                                      new ClipboardItem({
                                        [actualType]: blob
                                      })
                                    ]);
                                    copySuccess = true;
                                  }
                                }
                              } catch (clipboardError) {
                                console.log('Clipboard API method failed, trying fallback:', clipboardError);
                              }

                              // Method 2: If Clipboard API failed, try creating a temporary canvas image
                              if (!copySuccess) {
                                try {
                                  // Create a temporary image from canvas data URL
                                  const dataUrl = canvas.toDataURL('image/png');
                                  const tempImg = new Image();
                                  tempImg.crossOrigin = 'anonymous';

                                  await new Promise((resolve, reject) => {
                                    tempImg.onload = resolve;
                                    tempImg.onerror = reject;
                                    tempImg.src = dataUrl;

                                    // Timeout after 2 seconds
                                    setTimeout(() => reject(new Error('Image load timeout')), 2000);
                                  });

                                  // Create a new canvas from the temp image
                                  const tempCanvas = document.createElement('canvas');
                                  tempCanvas.width = tempImg.width;
                                  tempCanvas.height = tempImg.height;
                                  const tempCtx = tempCanvas.getContext('2d');
                                  tempCtx.drawImage(tempImg, 0, 0);

                                  // Try copying again with the new blob
                                  tempCanvas.toBlob(async (newBlob) => {
                                    if (newBlob && navigator.clipboard && window.ClipboardItem) {
                                      try {
                                        await navigator.clipboard.write([
                                          new ClipboardItem({
                                            'image/png': newBlob
                                          })
                                        ]);
                                        copySuccess = true;
                                      } catch (e) {
                                        console.log('Second clipboard attempt failed:', e);
                                      }
                                    }

                                    if (!copySuccess) {
                                      // Last resort: download
                                      const url = URL.createObjectURL(blob);
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.download = 'design-image.png';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                      URL.revokeObjectURL(url);
                                      alert('Image downloaded. Clipboard copy may not be supported in this browser. Please use right-click > Copy image as an alternative.');
                                    }
                                  }, 'image/png');

                                  return; // Exit early if using this method
                                } catch (fallbackError) {
                                  console.log('Fallback method failed:', fallbackError);
                                }
                              }

                              // If all methods failed, download as last resort
                              if (!copySuccess) {
                                const url = URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = 'design-image.png';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                URL.revokeObjectURL(url);
                                alert('Image downloaded. Clipboard copy not available. Please use right-click > Copy image.');
                              }
                            }, 'image/png');
                          } catch (e) {
                            console.error('Copy image failed:', e);
                            alert('Failed to copy image. Please try right-click > Copy image.');
                          }
                        }}
                        sx={{ ...detailIconButtonSx, display: (() => { const _s = getTransactionSummaryValues(selectedTransaction, transactionDesignDetails, terminalAddressDetails); return (_s?.placementType?.toLowerCase() === 'aerial' && _s?.dropType?.toLowerCase() === 'aerial') ? 'none' : undefined; })() }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                      </Box>
                    </Box>
                    <Card>
                      <CardContent>
                        {(() => {
                          const sv = getTransactionSummaryValues(selectedTransaction, transactionDesignDetails, terminalAddressDetails);
                          const isAerialBoth =
                            sv?.placementType?.toLowerCase() === 'aerial' &&
                            sv?.dropType?.toLowerCase() === 'aerial';

                          if (isAerialBoth) {
                            return (
                              <Box sx={{
                                width: '100%',
                                height: 300,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 2,
                                border: `2px dashed ${pagePalette.border}`,
                                bgcolor: '#FBFDFF',
                                p: 4,
                              }}>
                                <Typography sx={{ color: '#667085', fontSize: '0.95rem', textAlign: 'center', fontStyle: 'italic' }}>
                                  As Placement Type is Aerial &amp; Drop Type is Aerial, the buried drop design is not applicable.
                                </Typography>
                              </Box>
                            );
                          }

                          // Use the design details from the API call instead of searching the table
                          const base64 = transactionDesignDetails?.imagebase64;
                          const imageUrl = base64
                            ? (base64.startsWith('data:') ? base64 : `data:image/jpeg;base64,${base64}`)
                            : transactionDesignDetails?.converted_image;

                          return (
                            <Box>
                              {imageUrl ? (
                                <Box sx={{
                                  width: '100%',
                                  height: 400,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: `1px solid ${pagePalette.border}`,
                                  borderRadius: 2,
                                  bgcolor: '#FBFDFF',
                                  overflow: 'hidden',
                                  position: 'relative'
                                }}>
                                  <IconButton
                                    size="small"
                                    aria-label="Enlarge"
                                    onClick={() => { setImagePreviewSrc(imageUrl); setImagePreviewOpen(true); }}
                                    sx={{ ...detailIconButtonSx, position: 'absolute', top: 8, right: 8 }}
                                  >
                                    <OpenInFullIcon fontSize="small" />
                                  </IconButton>
                                  <img
                                    ref={designImageRef}
                                    src={imageUrl}
                                    alt="Design Image"
                                    style={{
                                      maxWidth: '100%',
                                      maxHeight: '350px',
                                      objectFit: 'contain',
                                      cursor: 'zoom-in'
                                    }}
                                    onClick={() => { setImagePreviewSrc(imageUrl); setImagePreviewOpen(true); }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'block';
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    color="error"
                                    sx={{ display: 'none', textAlign: 'center', mt: 2 }}
                                  >
                                    Failed to load design image
                                  </Typography>
                                </Box>
                              ) : drawerMediaQuery.isPending ? (
                                <Box aria-label="Loading design image" sx={{ width: '100%' }}>
                                  <Skeleton variant="rounded" height={300} animation="wave" />
                                </Box>
                              ) : (
                                <Box sx={{
                                  width: '100%',
                                  height: 300,
                                  background: 'linear-gradient(135deg, #FFFFFF 0%, #F6FBFF 58%, #F0FAF8 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: 2,
                                  border: `2px dashed ${pagePalette.border}`
                                }}>
                                  <Typography variant="body2" color="text.secondary">
                                    No design image available for this transaction
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          );
                        })()}
                      </CardContent>
                    </Card>

                    {/* Processed Images Gallery */}
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" sx={{ color: pagePalette.ink, fontWeight: 800, mb: 2 }}>
                        Processed Images
                      </Typography>
                      <Card>
                        <CardContent>
                          {drawerMediaQuery.isPending ? (
                            <Box sx={{ display: 'grid', gap: 1.5 }} aria-label="Loading processed images">
                              <Skeleton variant="rounded" height={180} animation="wave" />
                              <Skeleton width="42%" height={20} animation="wave" />
                            </Box>
                          ) : drawerMediaQuery.isError ? (
                            <Alert
                              severity="error"
                              action={<Button color="inherit" size="small" onClick={() => drawerMediaQuery.refetch()}>Retry</Button>}
                            >
                              Processed images could not be loaded.
                            </Alert>
                          ) : allProcessedImages && Object.keys(allProcessedImages).length > 0 ? (
                            Object.entries(allProcessedImages).flatMap(([role, payloads]) => {
                              if (!STREETVIEW_DISPLAY_ROLES.has(role)) return [];
                              return (Array.isArray(payloads) ? payloads : [payloads]).map((payload, idx) => {
                                const imgUrl = getSignedOrRawImageUrl(payload);
                                if (!imgUrl) return null;
                                const label = ROLE_LABELS[role] || role;
                                return (
                                  <Box key={`${role}-${idx}`} sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                      {label}
                                    </Typography>
                                    <Box sx={{
                                      position: 'relative',
                                      border: `1px solid ${pagePalette.border}`,
                                      borderRadius: 2,
                                      bgcolor: '#FBFDFF',
                                      overflow: 'hidden',
                                    }}>
                                      <IconButton
                                        size="small"
                                        aria-label={`Download ${label}`}
                                        onClick={() => downloadImage(imgUrl, `${role}-${idx}-${selectedTransaction?.row_id || 'image'}.jpg`)}
                                        sx={{ ...detailIconButtonSx, position: 'absolute', top: 8, right: 52 }}
                                      >
                                        <FileDownloadIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        aria-label="Enlarge"
                                        onClick={() => { setImagePreviewSrc(imgUrl); setImagePreviewOpen(true); }}
                                        sx={{ ...detailIconButtonSx, position: 'absolute', top: 8, right: 8 }}
                                      >
                                        <OpenInFullIcon fontSize="small" />
                                      </IconButton>
                                      <img
                                        src={imgUrl}
                                        alt={label}
                                        style={{ width: '100%', maxHeight: 350, objectFit: 'contain', display: 'block', cursor: 'zoom-in' }}
                                        onClick={() => { setImagePreviewSrc(imgUrl); setImagePreviewOpen(true); }}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                      />
                                    </Box>
                                  </Box>
                                );
                              });
                            })
                          ) : (
                            <Box sx={{
                              width: '100%',
                              height: 200,
                              background: 'linear-gradient(135deg, #FFFFFF 0%, #F6FBFF 58%, #F0FAF8 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 2,
                              border: `2px dashed ${pagePalette.border}`
                            }}>
                              <Typography variant="body2" color="text.secondary">
                                No processed images available for this transaction.
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Box>

                  </TabPanel>

                  {/* Details Tab - Design Details + Terminal Address */}
                  <TabPanel value={activeTab} index={2}>
                    {drawerDetailQuery.isPending && (
                      <Box sx={{ display: 'grid', gap: 1, mb: 2 }} aria-label="Loading transaction details">
                        <Skeleton width="35%" height={24} animation="wave" />
                        <Skeleton variant="rounded" height={96} animation="wave" />
                      </Box>
                    )}
                    <Typography sx={{ fontWeight: 800, color: pagePalette.ink, mb: 1, fontSize: '0.88rem', letterSpacing: '-0.01em' }}>Design Details</Typography>
                    <Card sx={{ mb: 0.5 }}>
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: pagePalette.ink, fontSize: '0.78rem' }}>TDP / Temp Drop Result</Typography>
                          <Chip label={`TDP: ${getTdpStatus(transactionDesignDetails)}`} size="small" variant="outlined" sx={{ fontWeight: 800, fontSize: '0.68rem', ...getStatusChipSx(getTdpStatus(transactionDesignDetails)) }} />
                        </Box>
                        <Table size="small">
                          <TableBody>
                            {[
                              ['Temp Drop', tempDropLabel(formatValue(transactionDesignDetails?.tdp_temp_drop))],
                              ['Aerial', formatValue(transactionDesignDetails?.tdp_aerial)],
                              ['Hybrid', formatValue(transactionDesignDetails?.tdp_hybrid)],
                              ['Scenario', formatValue(transactionDesignDetails?.tdp_scenario)],
                              ...(transactionDesignDetails?.tdp_raw?.message ? [['TDP Message', transactionDesignDetails.tdp_raw.message]] : []),
                            ].map(([label, value]) => (
                              <TableRow key={label} sx={{ '&:last-child td': { border: 0 } }}>
                                <TableCell sx={{ fontWeight: 700, color: pagePalette.ink, width: 160, py: 0.4, px: 1.5, fontSize: '0.76rem', borderColor: pagePalette.border }}>{label}</TableCell>
                                <TableCell sx={{ color: pagePalette.ink, py: 0.4, px: 1.5, fontSize: '0.76rem', borderColor: pagePalette.border }}>{value}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                    <Card sx={{ mb: 0.5 }}>
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: pagePalette.ink, fontSize: '0.78rem' }}>Drop Classifier Result</Typography>
                          <Chip label={`Classifier: ${formatValue(selectedTransaction?.drop_classifier_status)}`} size="small" variant="outlined" sx={{ fontWeight: 800, fontSize: '0.68rem', ...getStatusChipSx(selectedTransaction?.drop_classifier_status || '') }} />
                        </Box>
                        <Table size="small">
                          <TableBody>
                            {[
                              ['Placement', formatValue(selectedTransaction?.drop_classifier_result)],
                              ['Confidence', formatValue(selectedTransaction?.drop_classifier_raw?.confidence)],
                              ['Evidence', formatValue(selectedTransaction?.drop_classifier_raw?.evidence)],
                              ...(selectedTransaction?.drop_classifier_error ? [['Error', selectedTransaction.drop_classifier_error]] : []),
                            ].map(([label, value]) => (
                              <TableRow key={label} sx={{ '&:last-child td': { border: 0 } }}>
                                <TableCell sx={{ fontWeight: 700, color: pagePalette.ink, width: 160, py: 0.4, px: 1.5, fontSize: '0.76rem', borderColor: pagePalette.border }}>{label}</TableCell>
                                <TableCell sx={{ color: pagePalette.ink, py: 0.4, px: 1.5, fontSize: '0.76rem', borderColor: pagePalette.border }}>{value}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                    <Card sx={{ mb: 2 }}>
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <Table size="small">
                          <TableBody>
                            {[
                              ['Output Row ID', transactionDesignDetails?.row_id !== null && transactionDesignDetails?.row_id !== undefined ? transactionDesignDetails.row_id : 'N/A'],
                              ['Address ID', transactionDesignDetails?.address_id || 'N/A'],
                              ['TDP Scenario', formatValue(transactionDesignDetails?.tdp_scenario)],
                              ['Due Date Interval', formatValue(transactionDesignDetails?.tdp_due_date_interval)],
                              ['Overall Status', transactionDesignDetails?.status || 'N/A'],
                              ['Flowerpots', transactionDesignDetails?.flowerpots !== null && transactionDesignDetails?.flowerpots !== undefined ? transactionDesignDetails.flowerpots : 'N/A'],
                              ['Flowerpot ID', transactionDesignDetails?.fp_id || 'N/A'],
                              ['Building ID', transactionDesignDetails?.building_id !== null && transactionDesignDetails?.building_id !== undefined ? transactionDesignDetails.building_id : 'N/A'],
                              ['Version ID', transactionDesignDetails?.version_id !== null && transactionDesignDetails?.version_id !== undefined ? transactionDesignDetails.version_id : 'N/A'],
                              ['Is Current', transactionDesignDetails?.is_current !== null && transactionDesignDetails?.is_current !== undefined ? (transactionDesignDetails.is_current ? 'Yes' : 'No') : 'N/A'],
                              ['Summary Date', transactionDesignDetails?.summary_date || 'N/A'],
                            ].map(([label, value]) => (
                              <TableRow key={label} sx={{ '&:last-child td': { border: 0 } }}>
                                <TableCell sx={{ fontWeight: 700, color: pagePalette.ink, width: 160, py: 0.4, px: 1.5, fontSize: '0.76rem', borderColor: pagePalette.border }}>{label}</TableCell>
                                <TableCell sx={{ color: pagePalette.ink, py: 0.4, px: 1.5, fontSize: '0.76rem', borderColor: pagePalette.border, wordBreak: 'break-all' }}>{value}</TableCell>
                              </TableRow>
                            ))}
                            {transactionDesignDetails?.converted_image && (
                              <TableRow sx={{ '&:last-child td': { border: 0 } }}>
                                <TableCell sx={{ fontWeight: 700, color: pagePalette.ink, width: 160, py: 0.4, px: 1.5, fontSize: '0.76rem', borderColor: pagePalette.border }}>Converted Image</TableCell>
                                <TableCell sx={{ py: 0.4, px: 1.5, fontSize: '0.76rem', borderColor: pagePalette.border, wordBreak: 'break-all' }}>
                                  <a href={transactionDesignDetails.converted_image} target="_blank" rel="noopener noreferrer" style={{ color: pagePalette.primary, wordBreak: 'break-all' }}>{transactionDesignDetails.converted_image}</a>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>

                    <Typography sx={{ fontWeight: 800, color: pagePalette.ink, mb: 1, fontSize: '0.88rem', letterSpacing: '-0.01em' }}>Terminal Address Details</Typography>
                    <Card>
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <Table size="small">
                          <TableBody>
                            {[
                              ['Row Number', terminalAddressDetails?.row_num || 'N/A'],
                              ['Ticket ID', terminalAddressDetails?.ticket_id || 'N/A'],
                              ['Terminal ID', terminalAddressDetails?.terminal_id || 'N/A'],
                              ['Address ID', terminalAddressDetails?.address_id || 'N/A'],
                            ].map(([label, value]) => (
                              <TableRow key={label} sx={{ '&:last-child td': { border: 0 } }}>
                                <TableCell sx={{ fontWeight: 700, color: pagePalette.ink, width: 160, py: 0.4, px: 1.5, fontSize: '0.76rem', borderColor: pagePalette.border }}>{label}</TableCell>
                                <TableCell sx={{ color: pagePalette.ink, py: 0.4, px: 1.5, fontSize: '0.76rem', borderColor: pagePalette.border }}>{value}</TableCell>
                              </TableRow>
                            ))}
                            {(() => {
                              const rows = [];
                              for (let i = 1; i <= 8; i++) {
                                const addr = terminalAddressDetails?.[`addr${i}`];
                                const lat = terminalAddressDetails?.[`addr${i}_lat`];
                                const lng = terminalAddressDetails?.[`addr${i}_long`];
                                const hasAny = (addr && String(addr).trim() !== '') || typeof lat === 'number' || typeof lng === 'number';
                                if (!hasAny) continue;
                                if (i === 1) {
                                  const tLat = terminalAddressDetails?.terminal_lat;
                                  const tLng = terminalAddressDetails?.terminal_long;
                                  rows.push(
                                    <TableRow key="terminal-coords">
                                      <TableCell sx={{ fontWeight: 700, color: pagePalette.ink, width: 160, py: 0.4, px: 1.5, fontSize: '0.76rem', borderColor: pagePalette.border }}>Terminal Coords</TableCell>
                                      <TableCell sx={{ color: pagePalette.ink, py: 0.4, px: 1.5, fontSize: '0.76rem', borderColor: pagePalette.border }}>
                                        {typeof tLat === 'number' ? `${tLat.toFixed(6)}, ${typeof tLng === 'number' ? tLng.toFixed(6) : 'N/A'}` : 'N/A'}
                                      </TableCell>
                                    </TableRow>
                                  );
                                }
                                rows.push(
                                  <TableRow key={`addr${i}`}>
                                    <TableCell sx={{ fontWeight: 700, color: pagePalette.ink, width: 160, py: 0.4, px: 1.5, fontSize: '0.76rem', borderColor: pagePalette.border }}>{`Address ${i}`}</TableCell>
                                    <TableCell sx={{ color: pagePalette.ink, py: 0.4, px: 1.5, fontSize: '0.76rem', borderColor: pagePalette.border }}>
                                      {addr || 'N/A'}{typeof lat === 'number' ? ` · ${lat.toFixed(6)}, ${typeof lng === 'number' ? lng.toFixed(6) : 'N/A'}` : ''}
                                    </TableCell>
                                  </TableRow>
                                );
                              }
                              return rows.length ? rows : (
                                <TableRow><TableCell colSpan={2} sx={{ color: pagePalette.muted, fontSize: '0.76rem', py: 0.4, px: 1.5 }}>No address details available</TableCell></TableRow>
                              );
                            })()}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  </TabPanel>

                  {/* Map Tab */}
                  <TabPanel value={activeTab} index={1}>
                    <Card>
                      <CardContent>
                        {(() => {
                          const streetView = buildGoogleStreetViewUrls(selectedTransaction, terminalAddressDetails);
                          const dropMarkers = buildDropMarkersUrls(selectedTransaction, terminalAddressDetails);
                          const homeStreetView = buildStreetViewLinksForPoint(dropMarkers.home);
                          const terminalStreetView = buildStreetViewLinksForPoint(dropMarkers.terminal);
                          const hasMarkers = Boolean(dropMarkers.home || dropMarkers.terminal);

                          const activeView =
                            streetMapMode === 'home' && homeStreetView.embedUrl
                              ? { ...homeStreetView, caption: `Street View — home address${streetView.address ? `: ${streetView.address}` : ''}` }
                              : streetMapMode === 'terminal' && terminalStreetView.embedUrl
                                ? { ...terminalStreetView, caption: 'Street View — terminal location' }
                                : hasMarkers
                                  ? {
                                      pointerMap: true,
                                      mapUrl: dropMarkers.mapUrl,
                                      caption: dropMarkers.home && dropMarkers.terminal
                                        ? 'Yellow dot: terminal · Red pin: home address — click a pointer or button to open Street View'
                                        : streetView.address || 'Map location',
                                    }
                                  : { ...streetView, caption: streetView.address || 'Street View location' };

                          return (activeView.pointerMap || activeView.embedUrl) ? (
                            <Box>
                              {hasMarkers && (
                                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                                  <Button
                                    size="small"
                                    variant={streetMapMode === 'map' ? 'contained' : 'outlined'}
                                    onClick={() => setStreetMapMode('map')}
                                    sx={streetMapMode === 'map' ? { textTransform: 'none' } : secondaryButtonSx}
                                  >
                                    Aerial View
                                  </Button>
                                  {dropMarkers.home && (
                                    <Button
                                      size="small"
                                      variant={streetMapMode === 'home' ? 'contained' : 'outlined'}
                                      onClick={() => setStreetMapMode('home')}
                                      sx={streetMapMode === 'home' ? { textTransform: 'none' } : secondaryButtonSx}
                                    >
                                      Home Street View
                                    </Button>
                                  )}
                                  {dropMarkers.terminal && (
                                    <Button
                                      size="small"
                                      variant={streetMapMode === 'terminal' ? 'contained' : 'outlined'}
                                      onClick={() => setStreetMapMode('terminal')}
                                      sx={streetMapMode === 'terminal' ? { textTransform: 'none' } : secondaryButtonSx}
                                    >
                                      Terminal Street View
                                    </Button>
                                  )}
                                </Box>
                              )}
                              <Box sx={{
                                width: '100%',
                                height: 420,
                                border: `1px solid ${pagePalette.border}`,
                                borderRadius: 2,
                                bgcolor: '#FBFDFF',
                                overflow: 'hidden',
                              }}>
                                {activeView.pointerMap ? (
                                  <DropMarkersLeafletMap
                                    homeLat={dropMarkers.home?.latitude ?? null}
                                    homeLng={dropMarkers.home?.longitude ?? null}
                                    terminalLat={dropMarkers.terminal?.latitude ?? null}
                                    terminalLng={dropMarkers.terminal?.longitude ?? null}
                                    onMarkerClick={setStreetMapMode}
                                  />
                                ) : (
                                  <iframe
                                    key={activeView.embedUrl}
                                    title={`Google Street Map for ${streetView.address || selectedTransaction?.ticket_id || 'selected transaction'}`}
                                    src={activeView.embedUrl}
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0, display: 'block' }}
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    allowFullScreen
                                  />
                                )}
                              </Box>
                              <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 1.5,
                                mt: 1.5,
                              }}>
                                <Typography variant="body2" sx={{ color: pagePalette.muted, wordBreak: 'break-word' }}>
                                  {activeView.caption}
                                </Typography>
                                <Button
                                  component="a"
                                  href={activeView.mapUrl || streetView.mapUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  size="small"
                                  variant="outlined"
                                  sx={{ ...secondaryButtonSx, whiteSpace: 'nowrap' }}
                                >
                                  Open in Google Maps
                                </Button>
                              </Box>
                            </Box>
                          ) : (
                            <Box sx={{
                              width: '100%',
                              height: 200,
                              background: 'linear-gradient(135deg, #FFFFFF 0%, #F6FBFF 58%, #F0FAF8 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 2,
                              border: `2px dashed ${pagePalette.border}`
                            }}>
                              <Typography variant="body2" color="text.secondary">
                                No address available for Google Street View.
                              </Typography>
                            </Box>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  </TabPanel>

                  {/* Payload Details Tab */}
                  <TabPanel value={activeTab} index={3}>
                    <Typography variant="h6" sx={{ mb: 2, color: pagePalette.ink, fontWeight: 800 }}>
                      Payload Details
                    </Typography>
                    <Card sx={{ position: 'relative' }}>
                      <CardContent>
                        {(() => {
                          if (drawerPayloadQuery.isPending) {
                            return (
                              <Box sx={{ display: 'grid', gap: 1 }} aria-label="Loading payload">
                                <Skeleton width="38%" height={24} animation="wave" />
                                <Skeleton variant="rounded" height={240} animation="wave" />
                              </Box>
                            );
                          }
                          if (drawerPayloadQuery.isError) {
                            return (
                              <Alert
                                severity="error"
                                action={<Button color="inherit" size="small" onClick={() => drawerPayloadQuery.refetch()}>Retry</Button>}
                              >
                                Payload could not be loaded.
                              </Alert>
                            );
                          }
                          const raw = selectedTransaction?.payload ?? null;
                          let payloadObj = null;
                          if (raw) {
                            if (typeof raw === 'string') {
                              try { payloadObj = JSON.parse(raw); } catch { payloadObj = raw; }
                            } else {
                              payloadObj = raw;
                            }
                          }

                          if (!payloadObj) {
                            return (
                              <Typography variant="body2" color="text.secondary">
                                No payload available for this transaction
                              </Typography>
                            );
                          }

                          const copyPayload = async () => {
                            try {
                              const text = typeof payloadObj === 'string' ? payloadObj : JSON.stringify(payloadObj, null, 2);
                              await navigator.clipboard.writeText(text);
                            } catch (e) {
                              console.error('Copy failed:', e);
                            }
                          };

                          // Pretty line-by-line JSON display
                          return (
                            <Box>
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                <IconButton
                                  aria-label="Copy payload"
                                  size="small"
                                  onClick={copyPayload}
                                  sx={detailIconButtonSx}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              <Box sx={{
                                p: 2,
                                border: `1px solid ${pagePalette.border}`,
                                borderRadius: 2,
                                backgroundColor: '#FBFDFF',
                                overflowX: 'auto'
                              }}>
                                <Typography
                                  component="pre"
                                  sx={{
                                    m: 0,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                    fontSize: '0.85rem',
                                    lineHeight: 1.5,
                                    color: '#1a1a1a'
                                  }}
                                >
                                  {typeof payloadObj === 'string' ? payloadObj : JSON.stringify(payloadObj, null, 2)}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  </TabPanel>
              </>
            </Box>
          </Box>
        )}
      </Drawer>

      <Dialog open={transactionToProcess !== null} onClose={() => setTransactionToProcess(null)} fullWidth maxWidth="xs">
        <DialogTitle>Process transaction {transactionToProcess}</DialogTitle>
        <DialogContent>
          <RadioGroup value={transactionProcessingMode} onChange={(event) => setTransactionProcessingMode(event.target.value)}>
            <FormControlLabel value="tdp_bsw" control={<Radio />} label="TDP and BSW design" />
            <FormControlLabel value="tdp_only" control={<Radio />} label="TDP only" />
            <FormControlLabel value="bsw_only" control={<Radio />} label="BSW design only" />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransactionToProcess(null)}>Cancel</Button>
          <Button variant="contained" onClick={() => handleSubmitAction(transactionToProcess)} disabled={submittingTransactions.has(transactionToProcess)}>
            {submittingTransactions.has(transactionToProcess) ? <CircularProgress size={20} /> : 'Queue transaction'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={imagePreviewOpen} onClose={() => setImagePreviewOpen(false)} fullWidth maxWidth="xl">
        <Box sx={{ position: 'relative' }}>
          <IconButton
            aria-label="Minimize"
            onClick={() => setImagePreviewOpen(false)}
            size="small"
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: '#60A5FA', color: '#fff', '&:hover': { bgcolor: '#3B82F6' }, width: 32, height: 32 }}
          >
            <CloseFullscreenIcon fontSize="small" />
          </IconButton>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {imagePreviewSrc && (
              <img
                src={imagePreviewSrc}
                alt="Design Preview"
                style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
              />
            )}
          </Box>
        </Box>
      </Dialog>

      <DesignEditPane
        open={designEditOpen}
        rowId={selectedRowId}
        onClose={() => setDesignEditOpen(false)}
        onSaved={() => {
          // Pull the newly saved version into the drawer.
          if (selectedRowId) {
            queryClient.invalidateQueries({ queryKey: drawerDetailKey(selectedRowId) });
          }
        }}
      />
    </Box>
  );
}
